<template>
  <div class="jim-scheduling-container pnl-page">

    <!-- Header row (matches your vibe) -->
    <div class="page-header-row">
      <div class="page-header-left"></div>
      <h1 class="page-heading">P&amp;L REPORT</h1>
      <div class="page-header-right"></div>
    </div>

    <div class="main-content">

      <!-- ✅ LEFT SIDE CONTROLS (Pablo style) -->
      <div class="left-side-section">

        <div class="control" style="width:100%;">
          <label>Start Date</label>
          <input v-model="startDate" type="date" />
        </div>

        <div class="control" style="width:100%;">
          <label>End Date</label>
          <input v-model="endDate" type="date" />
        </div>

        <div class="control" style="width:100%;">
          <label>CU</label>
          <select v-model="cu" class="select-input">
            <option value="ALL">ALL</option>
            <option value="00">00</option>
            <option value="01">01</option>
            <option value="02">02</option>
            <option value="03">03</option>
            <option value="04">04</option>
            <option value="05">05</option>
            <option value="09">09</option>
          </select>
        </div>

        <div class="control" style="width:100%;">
          <label>Options</label>
          <div class="toggle-row">
            <label class="toggle">
              <input type="checkbox" v-model="showPercent" />
              <span>Show %</span>
            </label>
            <label class="toggle">
              <input type="checkbox" v-model="showDetails" />
              <span>Show Detail</span>
            </label>
          </div>
        </div>

        <button class="plan-it-btn" :disabled="loading || !canRun" @click="runReport">
          {{ loading ? "Loading..." : "Run Report" }}
        </button>

        <button class="plan-it-btn" :disabled="!reportLoaded" @click="printPNL">
          Print
        </button>

        <button class="plan-it-btn" :disabled="!reportLoaded" @click="exportCSV">
          Export CSV
        </button>

        <button class="plan-it-btn" :disabled="!reportLoaded" @click="openAnalysis">
          OPEX Analysis
        </button>

        <button class="plan-it-btn pablo-take-btn" :disabled="!reportLoaded" @click="openPabloTake">
          PABLO VIEW
        </button>

        <!-- ✅ Unmapped accounts -->
        <div v-if="reportLoaded && (mtdReport?.unmapped_accounts?.length || 0) > 0"
            class="panel warn-box"
            style="margin-top:10px;">
          <div class="warn-title">
            Unmapped Accounts ({{ mtdReport.unmapped_accounts.length }})
          </div>
          <div class="warn-body">
            <div class="warn-list">
              <span v-for="a in mtdReport.unmapped_accounts" :key="a" class="warn-chip">
                {{ a }}
              </span>
            </div>
            <div class="small-muted" style="margin-top:8px;">
              These accounts are included in totals only if your backend logic sums them via section filters.
              Add them to <code>gl_pnl_map</code> to control display + ordering.
            </div>
          </div>
        </div>


      </div>

      <!-- ✅ RIGHT SIDE CONTENT (scrollable) -->
      <div class="right-side-section">

        <!-- Summary bar -->
        <div v-if="reportLoaded" class="summary-bar">
          <div class="summary-item">
            <div class="summary-label">MTD</div>
            <div class="summary-value">{{ mtdReport.startDate }} → {{ mtdReport.endDate }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">YTD</div>
            <div class="summary-value">{{ ytdReport.startDate }} → {{ ytdReport.endDate }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">LYTD</div>
            <div class="summary-value">{{ lytdReport.startDate }} → {{ lytdReport.endDate }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">CU</div>
            <div class="summary-value">{{ mtdReport.cu }}</div>
          </div>
        </div>

        <!-- Scrollable content wrapper -->
        <div v-if="reportLoaded" class="sheet-scroll-container">
          <div class="sheet-wrapper">

            <div class="panel report-panel">

              <div class="table-scroll">
                <table class="pnl-table">
                  <thead>
                    <tr>
                      <th class="sticky-col line-col">Line</th>
                      <th class="month-col">{{ displayMonthLabel }}</th>
                      <th class="pct-col">%</th>
                      <th class="month-col">YTD</th>
                      <th class="pct-col">%</th>
                      <th class="month-col">LYTD</th>
                      <th class="pct-col">%</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="row in renderedRows"
                      :key="row.key"
                      :data-pnl-row="row.key"
                      :class="[rowClass(row), { 'pnl-focus-pulse': focusedRowKey === row.key }]"
                    >
                      <td class="sticky-col line-col">
                        <span :class="{ indent: row.indent }">{{ row.label }}</span>
                      </td>

                      <!-- MTD -->
                      <td class="amt-cell clickable-cell"
                          @click="isClickable(row) && openDrilldown(row, 'MTD')">
                        <span v-if="cellAmount(row, monthObj) !== null">
                          {{ formatMoney(cellAmount(row, monthObj)) }}
                        </span>
                        <span v-else class="dash">-</span>
                      </td>

                      <td class="pct-cell">
                        <span v-if="cellPercent(row, monthObj) !== null">{{ formatPercent(cellPercent(row, monthObj)) }}</span>
                        <span v-else class="dash">-</span>
                      </td>

                      <!-- YTD -->
                      <td class="amt-cell clickable-cell"
                          @click="isClickable(row) && openDrilldown(row, 'YTD')">
                        <span v-if="cellAmount(row, ytdTotalsObj) !== null">
                          {{ formatMoney(cellAmount(row, ytdTotalsObj)) }}
                        </span>
                        <span v-else class="dash">-</span>
                      </td>

                      <td class="pct-cell">
                        <span v-if="cellPercent(row, ytdTotalsObj) !== null">{{ formatPercent(cellPercent(row, ytdTotalsObj)) }}</span>
                        <span v-else class="dash">-</span>
                      </td>

                      <!-- LYTD -->
                      <td class="amt-cell clickable-cell"
                          @click="isClickable(row) && openDrilldown(row, 'LYTD')">
                        <span v-if="cellAmount(row, lytdTotalsObj) !== null">
                          {{ formatMoney(cellAmount(row, lytdTotalsObj)) }}
                        </span>
                        <span v-else class="dash">-</span>
                      </td>

                      <td class="pct-cell">
                        <span v-if="cellPercent(row, lytdTotalsObj) !== null">{{ formatPercent(cellPercent(row, lytdTotalsObj)) }}</span>
                        <span v-else class="dash">-</span>
                      </td>
                    </tr>
                  </tbody>

                </table>
              </div>

              <!-- quick note -->
              <div class="small-muted" style="margin-top:10px;">
                
                Percent columns use <code>percent_of_gross_sales</code> when available.
              </div>

            </div>
          </div>
        </div>

        <div v-else class="panel hint-panel">
          Select a date range and CU, then click <b>Run Report</b>.
        </div>

      </div>
    </div>

    <!-- Executive Dashboard Modal -->
    <div v-if="pabloTakeOpen" class="modal-overlay executive-modal-overlay" @click.self="closePabloTake">
      <div class="modal panel executive-dashboard-modal">
        <div class="modal-header pablo-modal-header executive-modal-header">
          <div>
            <div class="pablo-modal-kicker">PABLO EXECUTIVE DASHBOARD</div>
            <div class="modal-title pablo-modal-title">Executive View</div>
            <div class="small-muted pablo-modal-subtitle">
              {{ displayMonthLabel }} • CU {{ cu }} • YTD through {{ endDate }}
            </div>
          </div>
          <button class="modal-close executive-modal-close" @click="closePabloTake">✕</button>
        </div>

        <section class="executive-dashboard executive-dashboard-in-modal">
          <div class="executive-kpi-grid">
            <button
              v-for="card in executiveKpis"
              :key="card.key"
              type="button"
              class="executive-kpi-card"
              :class="`tone-${card.tone}`"
              @click="handleExecutiveKpi(card)"
            >
              <div class="executive-kpi-label">{{ card.label }}</div>
              <div class="executive-kpi-value">{{ card.value }}</div>
              <div class="executive-kpi-change">
                <span class="executive-kpi-arrow">{{ card.arrow }}</span>
                {{ card.comparison }}
              </div>
              <div class="executive-kpi-hint">click to locate in P&amp;L</div>
            </button>
          </div>

          <div class="executive-chart-grid">
            <div class="executive-chart-card">
              <div class="executive-card-heading-row">
                <div>
                  <div class="executive-card-kicker">TREND</div>
                  <div class="executive-card-title">Monthly Net Sales</div>
                </div>
                <div class="chart-legend">
                  <span><i class="legend-line legend-ty"></i>{{ currentYearLabel }}</span>
                  <span><i class="legend-line legend-ly"></i>{{ priorYearLabel }}</span>
                </div>
              </div>

              <svg class="executive-line-chart" viewBox="0 0 680 220" role="img" aria-label="Monthly net sales current year versus prior year">
                <g v-for="g in salesTrendChart.gridLines" :key="`sales-grid-${g.y}`">
                  <line class="chart-grid-line" x1="52" x2="660" :y1="g.y" :y2="g.y" />
                  <text class="chart-axis-label" x="46" :y="g.y + 4" text-anchor="end">{{ g.label }}</text>
                </g>
                <polyline v-if="salesTrendChart.lyPoints" :points="salesTrendChart.lyPoints" class="chart-line chart-line-ly" />
                <polyline v-if="salesTrendChart.tyPoints" :points="salesTrendChart.tyPoints" class="chart-line chart-line-ty" />
                <g v-for="p in salesTrendChart.lyDots" :key="`sales-ly-${p.index}`">
                  <circle class="chart-dot chart-dot-ly" :cx="p.x" :cy="p.y" r="3.5"><title>{{ p.tooltip }}</title></circle>
                </g>
                <g v-for="p in salesTrendChart.tyDots" :key="`sales-ty-${p.index}`">
                  <circle class="chart-dot chart-dot-ty" :cx="p.x" :cy="p.y" r="4"><title>{{ p.tooltip }}</title></circle>
                </g>
                <text
                  v-for="label in salesTrendChart.xLabels"
                  :key="`sales-x-${label.index}`"
                  class="chart-x-label"
                  :x="label.x"
                  y="210"
                  text-anchor="middle"
                >{{ label.label }}</text>
              </svg>
            </div>

            <div class="executive-chart-card">
              <div class="executive-card-heading-row">
                <div>
                  <div class="executive-card-kicker">MARGIN QUALITY • MONTHLY</div>
                  <div class="executive-card-title">Monthly Gross Margin %</div>
                </div>
                <div class="chart-legend">
                  <span><i class="legend-line legend-ty"></i>{{ currentYearLabel }}</span>
                  <span><i class="legend-line legend-ly"></i>{{ priorYearLabel }}</span>
                </div>
              </div>

              <svg class="executive-line-chart" viewBox="0 0 680 220" role="img" aria-label="Monthly gross margin current year versus prior year">
                <g v-for="g in grossMarginTrendChart.gridLines" :key="`margin-grid-${g.y}`">
                  <line class="chart-grid-line" x1="52" x2="660" :y1="g.y" :y2="g.y" />
                  <text class="chart-axis-label" x="46" :y="g.y + 4" text-anchor="end">{{ g.label }}</text>
                </g>
                <polyline v-if="grossMarginTrendChart.lyPoints" :points="grossMarginTrendChart.lyPoints" class="chart-line chart-line-ly" />
                <polyline v-if="grossMarginTrendChart.tyPoints" :points="grossMarginTrendChart.tyPoints" class="chart-line chart-line-ty" />
                <g v-for="p in grossMarginTrendChart.lyDots" :key="`margin-ly-${p.index}`">
                  <circle class="chart-dot chart-dot-ly" :cx="p.x" :cy="p.y" r="3.5"><title>{{ p.tooltip }}</title></circle>
                </g>
                <g v-for="p in grossMarginTrendChart.tyDots" :key="`margin-ty-${p.index}`">
                  <circle class="chart-dot chart-dot-ty" :cx="p.x" :cy="p.y" r="4"><title>{{ p.tooltip }}</title></circle>
                </g>
                <text
                  v-for="label in grossMarginTrendChart.xLabels"
                  :key="`margin-x-${label.index}`"
                  class="chart-x-label"
                  :x="label.x"
                  y="210"
                  text-anchor="middle"
                >{{ label.label }}</text>
              </svg>
            </div>

            <div class="executive-chart-card cumulative-margin-card">
              <div class="executive-card-heading-row cumulative-heading-row">
                <div>
                  <div class="executive-card-kicker cumulative-kicker">MARGIN QUALITY • CUMULATIVE YTD</div>
                  <div class="executive-card-title">Cumulative YTD Gross Margin %</div>
                  <div class="cumulative-chart-subtitle">
                    Each point includes January through that month — the final point ties directly to Pablo's YTD margin insight.
                  </div>
                </div>

                <div class="cumulative-margin-summary">
                  <div class="cumulative-end-pill current">
                    <span>{{ currentYearLabel }} YTD</span>
                    <strong>{{ formatPercent(executiveMetrics?.grossMarginYtd) }}</strong>
                  </div>
                  <div class="cumulative-end-pill prior">
                    <span>{{ priorYearLabel }} YTD</span>
                    <strong>{{ formatPercent(executiveMetrics?.grossMarginLytd) }}</strong>
                  </div>
                  <div class="cumulative-spread-pill" :class="`tone-${directionTone(executiveMetrics?.grossMarginPointChange)}`">
                    <span>YTD GAP</span>
                    <strong>{{ formatSignedPoints(executiveMetrics?.grossMarginPointChange) }}</strong>
                  </div>
                </div>
              </div>

              <div class="cumulative-chart-shell">
                <div class="chart-legend cumulative-chart-legend">
                  <span><i class="legend-line legend-ty"></i>{{ currentYearLabel }} cumulative</span>
                  <span><i class="legend-line legend-ly"></i>{{ priorYearLabel }} cumulative</span>
                </div>

                <svg class="executive-line-chart cumulative-margin-chart" viewBox="0 0 680 220" role="img" aria-label="Cumulative year to date gross margin current year versus prior year">
                  <g v-for="g in cumulativeGrossMarginTrendChart.gridLines" :key="`cum-margin-grid-${g.y}`">
                    <line class="chart-grid-line" x1="52" x2="660" :y1="g.y" :y2="g.y" />
                    <text class="chart-axis-label" x="46" :y="g.y + 4" text-anchor="end">{{ g.label }}</text>
                  </g>

                  <polyline v-if="cumulativeGrossMarginTrendChart.lyPoints" :points="cumulativeGrossMarginTrendChart.lyPoints" class="chart-line chart-line-ly" />
                  <polyline v-if="cumulativeGrossMarginTrendChart.tyPoints" :points="cumulativeGrossMarginTrendChart.tyPoints" class="chart-line chart-line-ty cumulative-current-line" />

                  <g v-for="p in cumulativeGrossMarginTrendChart.lyDots" :key="`cum-margin-ly-${p.index}`">
                    <circle class="chart-dot chart-dot-ly" :cx="p.x" :cy="p.y" r="3.5"><title>{{ p.tooltip }}</title></circle>
                  </g>
                  <g v-for="p in cumulativeGrossMarginTrendChart.tyDots" :key="`cum-margin-ty-${p.index}`">
                    <circle class="chart-dot chart-dot-ty" :cx="p.x" :cy="p.y" r="4"><title>{{ p.tooltip }}</title></circle>
                  </g>

                  <g v-if="cumulativeGrossMarginTrendChart.lyLast">
                    <circle class="chart-last-ring chart-last-ring-ly" :cx="cumulativeGrossMarginTrendChart.lyLast.x" :cy="cumulativeGrossMarginTrendChart.lyLast.y" r="8"></circle>
                    <text
                      class="chart-end-label chart-end-label-ly"
                      :x="cumulativeGrossMarginTrendChart.lyLast.x - 7"
                      :y="cumulativeGrossMarginTrendChart.lyLast.y - 11"
                      text-anchor="end"
                    >{{ formatPercent(cumulativeGrossMarginTrendChart.lyLast.value) }}</text>
                  </g>

                  <g v-if="cumulativeGrossMarginTrendChart.tyLast">
                    <circle class="chart-last-ring chart-last-ring-ty" :cx="cumulativeGrossMarginTrendChart.tyLast.x" :cy="cumulativeGrossMarginTrendChart.tyLast.y" r="9"></circle>
                    <text
                      class="chart-end-label chart-end-label-ty"
                      :x="cumulativeGrossMarginTrendChart.tyLast.x - 7"
                      :y="cumulativeGrossMarginTrendChart.tyLast.y + 19"
                      text-anchor="end"
                    >{{ formatPercent(cumulativeGrossMarginTrendChart.tyLast.value) }}</text>
                  </g>

                  <text
                    v-for="label in cumulativeGrossMarginTrendChart.xLabels"
                    :key="`cum-margin-x-${label.index}`"
                    class="chart-x-label"
                    :x="label.x"
                    y="210"
                    text-anchor="middle"
                  >{{ label.label }}</text>
                </svg>
              </div>
            </div>
          </div>

          <div class="executive-chart-card audit-watch-card">
            <div class="executive-card-heading-row audit-watch-heading">
              <div>
                <div class="executive-card-kicker audit-watch-kicker">FINANCE CONTROL • MTD ANOMALY SCAN</div>
                <div class="executive-card-title">Audit Watch</div>
                <div class="audit-watch-subtitle">
                  Current month versus the prior YTD monthly pattern and the same month last year. Scope is limited to accounts included through Operating Profit. Review items are unusual — not necessarily errors.
                </div>
              </div>

              <div class="audit-watch-status" :class="auditWatch.items.length ? 'review' : 'clean'">
                <span class="audit-watch-status-dot"></span>
                <strong>{{ auditWatch.items.length ? `${auditWatch.items.length} REVIEW ITEM${auditWatch.items.length === 1 ? '' : 'S'}` : 'NO MATERIAL ANOMALIES' }}</strong>
                <span>{{ displayMonthLabel }}</span>
              </div>
            </div>

            <div v-if="auditWatch.items.length" class="audit-watch-grid">
              <button
                v-for="(item, idx) in auditWatch.items"
                :key="`audit-${item.acctNo}`"
                type="button"
                class="audit-watch-item"
                :class="`severity-${item.severity}`"
                @click="openAuditDrilldown(item)"
              >
                <div class="audit-watch-item-top">
                  <div class="audit-watch-index">{{ String(idx + 1).padStart(2, '0') }}</div>
                  <div class="audit-watch-name-wrap">
                    <div class="audit-watch-name">{{ item.shortLabel }}</div>
                    <div class="audit-watch-account">{{ item.section }} • ACCT {{ item.acctNo }}</div>
                  </div>
                  <div class="audit-watch-severity">{{ item.severity === 'high' ? 'HIGH REVIEW' : 'REVIEW' }}</div>
                </div>

                <div class="audit-watch-copy">{{ item.text }}</div>

                <div class="audit-watch-metrics">
                  <div class="audit-watch-metric">
                    <span>CURRENT</span>
                    <strong>{{ formatCompactMoney(item.currentMagnitude) }}</strong>
                  </div>
                  <div class="audit-watch-metric">
                    <span>PRIOR AVG</span>
                    <strong>{{ formatCompactMoney(item.priorAvgMagnitude) }}</strong>
                  </div>
                  <div class="audit-watch-metric">
                    <span>{{ auditWatch.sameMonthLyLabel || 'SAME MO LY' }}</span>
                    <strong>{{ item.sameMonthLyMagnitude == null ? '-' : formatCompactMoney(item.sameMonthLyMagnitude) }}</strong>
                  </div>
                  <div class="audit-watch-metric">
                    <span>RATE VS PRIOR</span>
                    <strong :class="item.rateBps >= 0 ? 'audit-rate-up' : 'audit-rate-down'">{{ formatBps(item.rateBps) }}</strong>
                  </div>
                </div>

                <div class="audit-watch-action">DRILL INTO {{ displayMonthLabel }} →</div>
              </button>
            </div>

            <div v-else class="audit-watch-clean-state">
              <div class="audit-watch-clean-icon">✓</div>
              <div>
                <strong>No material account anomalies detected.</strong>
                <span>Pablo did not find an account that cleared the materiality and variance thresholds for review.</span>
              </div>
            </div>

            <div class="audit-watch-footnote">
              Heuristic control scan only. It can surface posting, accrual, classification, timing, or unusual-activity candidates; Finance still determines whether the activity is correct.
            </div>
          </div>

          <div class="executive-bottom-grid">
            <div class="executive-chart-card movers-card">
              <div class="executive-card-heading-row">
                <div>
                  <div class="executive-card-kicker">YTD VS LYTD</div>
                  <div class="executive-card-title">Top OPEX Movers</div>
                </div>
                <div class="mover-legend">click any account to drill down</div>
              </div>

              <div class="movers-columns">
                <div class="mover-column">
                  <div class="mover-column-title pressure">Cost Pressure</div>
                  <button
                    v-for="m in topOpexUnfavorable"
                    :key="`bad-${m.acctNo}`"
                    type="button"
                    class="mover-row"
                    @click="openMoverDrilldown(m)"
                  >
                    <div class="mover-row-top">
                      <span class="mover-name">{{ m.shortLabel }}</span>
                      <span class="mover-value bad">+{{ formatCompactMoney(m.deltaBurden) }}</span>
                    </div>
                    <div class="mover-track"><span class="mover-fill bad-fill" :style="{ width: `${m.barPct}%` }"></span></div>
                    <div class="mover-meta">{{ formatBps(m.bpsChange) }} vs LYTD</div>
                  </button>
                  <div v-if="topOpexUnfavorable.length === 0" class="empty-mover">No meaningful unfavorable movers.</div>
                </div>

                <div class="mover-column">
                  <div class="mover-column-title relief">Cost Relief</div>
                  <button
                    v-for="m in topOpexFavorable"
                    :key="`good-${m.acctNo}`"
                    type="button"
                    class="mover-row"
                    @click="openMoverDrilldown(m)"
                  >
                    <div class="mover-row-top">
                      <span class="mover-name">{{ m.shortLabel }}</span>
                      <span class="mover-value good">-{{ formatCompactMoney(m.absDelta) }}</span>
                    </div>
                    <div class="mover-track"><span class="mover-fill good-fill" :style="{ width: `${m.barPct}%` }"></span></div>
                    <div class="mover-meta">{{ formatBps(m.bpsChange) }} vs LYTD</div>
                  </button>
                  <div v-if="topOpexFavorable.length === 0" class="empty-mover">No meaningful favorable movers.</div>
                </div>
              </div>
            </div>

            <div class="executive-chart-card executive-brief-card">
              <div class="executive-card-heading-row">
                <div>
                  <div class="executive-card-kicker">EXECUTIVE BRIEF</div>
                  <div class="executive-card-title">Pablo's Take</div>
                </div>
                <div class="mover-legend">click an insight to show the source</div>
              </div>

              <div class="pablo-talking-points executive-talking-points">
                <button
                  v-for="(point, idx) in pabloTalkingPoints"
                  :key="point.key"
                  type="button"
                  class="pablo-point"
                  :class="{ clickable: !!point.acctNo || !!point.rowKey }"
                  @click="handleTalkingPoint(point)"
                >
                  <span class="pablo-point-number">{{ String(idx + 1).padStart(2, '0') }}</span>
                  <span class="pablo-point-body">
                    <span class="pablo-point-text">{{ point.text }}</span>
                    <span v-if="point.acctNo" class="pablo-point-action">DRILL INTO {{ point.acctNo }} →</span>
                    <span v-else-if="point.rowKey" class="pablo-point-action">LOCATE IN P&amp;L →</span>
                  </span>
                </button>
              </div>

              <div class="pablo-modal-footnote executive-brief-footnote">
                Generated from the report currently loaded. Account-based observations open the existing YTD drilldown.
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- ✅ OPEX Analysis Modal -->
    <div v-if="analysisOpen" class="modal-overlay" @click.self="closeAnalysis">
      <div class="modal panel">
        <div class="modal-header">
          <div class="modal-title">
            OPEX Fixed / Variable Analysis
          </div>

          <div style="display:flex; gap:8px; align-items:center;">
            <button class="modal-print-btn" @click="printOpexAnalysis">
              Print
            </button>
            <button class="modal-close" @click="closeAnalysis">✕</button>
          </div>
        </div>

        <div class="small-muted" style="margin-top:6px;">
          Date: <b>{{ startDate }}</b> → <b>{{ endDate }}</b> •
          CU: <b>{{ cu }}</b>
        </div>

        <div v-if="opexAnalysis" style="margin-top:10px;">

          <div class="panel" style="margin-bottom:10px;">
            <div class="warn-title">Operating Leverage / Profit Sensitivity</div>

              <div class="analysis-kpi-row">
                <div class="analysis-kpi">
                  <div class="summary-label">Estimated DOL - YTD</div>
                  <div class="summary-value">
                    {{ opexAnalysis.estimatedDolYtd == null ? "-" : formatRatio(opexAnalysis.estimatedDolYtd) }}
                  </div>
                </div>

                <div class="analysis-kpi">
                  <div class="summary-label">YTD vs LYTD Profit Sensitivity</div>
                  <div class="summary-value">
                    {{
                      opexAnalysis.observedProfitSensitivityYtdVsLytd == null
                        ? "-"
                        : formatRatio(opexAnalysis.observedProfitSensitivityYtdVsLytd)
                    }}
                  </div>
                </div>

                <div class="analysis-kpi">
                  <div class="summary-label">Sales Change</div>
                  <div class="summary-value">
                    {{ formatChangePercent(opexAnalysis.ytdVsLytd?.revenueChangePct) }}
                  </div>
                </div>

                <div class="analysis-kpi">
                  <div class="summary-label">Operating Profit Change</div>
                  <div class="summary-value">
                    {{ formatChangePercent(opexAnalysis.ytdVsLytd?.operatingProfitChangePct) }}
                  </div>
                </div>
              </div>

              <div class="small-muted" style="margin-top:8px;">
                <b>Estimated DOL</b> requires contribution margin from OE history and is shown only when available.
                True DOL = contribution margin / operating profit before depreciation.
              </div>

              <div class="small-muted" style="margin-top:6px;">
                <b>Profit Sensitivity</b> is an observed year-over-year metric:
                % change in operating profit before depreciation / % change in net sales.
                It can be negative when sales rise but operating profit falls.
              </div>
          </div>

          <div v-if="operatingProfitBridge" class="panel" style="margin-bottom:10px;">
            <div class="warn-title">YTD vs LYTD Operating Profit Bridge</div>

            <div class="table-scroll">
              <table class="mini-table">
                <thead>
                  <tr>
                    <th>Bridge Item</th>
                    <th style="text-align:right;">Impact</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="row in operatingProfitBridge.rows"
                    :key="row.key"
                    :class="bridgeRowClass(row)"
                  >
                    <td>{{ row.label }}</td>
                    <td style="text-align:right;">
                      <span v-if="row.type === 'impact'">
                        {{ formatBridgeImpact(row.amount) }}
                      </span>
                      <span v-else>
                        {{ formatMoney(row.amount) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              v-if="Math.abs(Number(operatingProfitBridge.checkDifference || 0)) > 0.01"
              class="small-muted"
              style="margin-top:8px;"
            >
              Bridge check difference:
              <b>{{ formatMoney(operatingProfitBridge.checkDifference) }}</b>
            </div>

            <div class="small-muted" style="margin-top:8px;">
              The COGS / Margin / 263A line explains the change between net sales growth and gross profit after 263A.
            </div>
          </div>



          <div class="panel">
            <div class="warn-title">Fixed / Variable OPEX</div>

            <div class="table-scroll">
              <table class="mini-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th style="text-align:right;">MTD</th>
                    <th style="text-align:right;">YTD</th>
                    <th style="text-align:right;">LYTD</th>
                    <th style="text-align:right;">YTD vs LYTD</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td>Net Sales</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.mtd.revenue) }}</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.ytd.revenue) }}</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.lytd.revenue) }}</td>
                    <td style="text-align:right;">{{ formatChangePercent(opexAnalysis.ytdVsLytd.revenueChangePct) }}</td>
                  </tr>

                  <tr>
                    <td>Fixed OPEX</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.mtd.fixedOpexBurden) }}</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.ytd.fixedOpexBurden) }}</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.lytd.fixedOpexBurden) }}</td>
                    <td style="text-align:right;">{{ formatChangePercent(opexAnalysis.ytdVsLytd.fixedOpexChangePct) }}</td>
                  </tr>

                  <tr>
                    <td>Fixed OPEX % of Revenue</td>
                    <td style="text-align:right;">{{ formatPercent(opexAnalysis.mtd.fixedOpexPctRevenue) }}</td>
                    <td style="text-align:right;">{{ formatPercent(opexAnalysis.ytd.fixedOpexPctRevenue) }}</td>
                    <td style="text-align:right;">{{ formatPercent(opexAnalysis.lytd.fixedOpexPctRevenue) }}</td>
                    <td style="text-align:right;">{{ formatPercentPoint(opexAnalysis.ytdVsLytd.fixedOpexPctPointChange) }}</td>
                  </tr>

                  <tr>
                    <td>Variable OPEX</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.mtd.variableOpexBurden) }}</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.ytd.variableOpexBurden) }}</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.lytd.variableOpexBurden) }}</td>
                    <td style="text-align:right;">{{ formatChangePercent(opexAnalysis.ytdVsLytd.variableOpexChangePct) }}</td>
                  </tr>

                  <tr>
                    <td>Variable OPEX % of Revenue</td>
                    <td style="text-align:right;">{{ formatPercent(opexAnalysis.mtd.variableOpexPctRevenue) }}</td>
                    <td style="text-align:right;">{{ formatPercent(opexAnalysis.ytd.variableOpexPctRevenue) }}</td>
                    <td style="text-align:right;">{{ formatPercent(opexAnalysis.lytd.variableOpexPctRevenue) }}</td>
                    <td style="text-align:right;">{{ formatPercentPoint(opexAnalysis.ytdVsLytd.variableOpexPctPointChange) }}</td>
                  </tr>

                  <tr>
                    <td>Total Classified OPEX</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.mtd.classifiedOpexBurden) }}</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.ytd.classifiedOpexBurden) }}</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.lytd.classifiedOpexBurden) }}</td>
                    <td style="text-align:right;">{{ formatChangePercent(opexAnalysis.ytdVsLytd.classifiedOpexChangePct) }}</td>
                  </tr>

                  <tr>
                    <td>Total Classified OPEX % of Revenue</td>
                    <td style="text-align:right;">{{ formatPercent(opexAnalysis.mtd.classifiedOpexPctRevenue) }}</td>
                    <td style="text-align:right;">{{ formatPercent(opexAnalysis.ytd.classifiedOpexPctRevenue) }}</td>
                    <td style="text-align:right;">{{ formatPercent(opexAnalysis.lytd.classifiedOpexPctRevenue) }}</td>
                    <td style="text-align:right;">{{ formatPercentPoint(opexAnalysis.ytdVsLytd.classifiedOpexPctPointChange) }}</td>
                  </tr>

                  <tr>
                    <td>Unclassified OPEX</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.mtd.unclassifiedOpexBurden) }}</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.ytd.unclassifiedOpexBurden) }}</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.lytd.unclassifiedOpexBurden) }}</td>
                    <td style="text-align:right;">{{ formatChangePercent(opexAnalysis.ytdVsLytd.unclassifiedOpexChangePct) }}</td>
                  </tr>

                  <tr>
                    <td>Operating Profit Before Depreciation</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.mtd.operatingProfitBeforeDepreciation) }}</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.ytd.operatingProfitBeforeDepreciation) }}</td>
                    <td style="text-align:right;">{{ formatMoney(opexAnalysis.lytd.operatingProfitBeforeDepreciation) }}</td>
                    <td style="text-align:right;">{{ formatChangePercent(opexAnalysis.ytdVsLytd.operatingProfitChangePct) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="panel" style="margin-top:10px;">
            <div class="warn-title">Classification Counts</div>

            <div class="small-muted">
              MTD:
              <b>{{ opexAnalysis.mtd.fixedAccountCount }}</b> fixed,
              <b>{{ opexAnalysis.mtd.variableAccountCount }}</b> variable,
              <b>{{ opexAnalysis.mtd.unclassifiedAccountCount }}</b> unclassified.
            </div>

            <div class="small-muted">
              YTD:
              <b>{{ opexAnalysis.ytd.fixedAccountCount }}</b> fixed,
              <b>{{ opexAnalysis.ytd.variableAccountCount }}</b> variable,
              <b>{{ opexAnalysis.ytd.unclassifiedAccountCount }}</b> unclassified.
            </div>
          </div>

        </div>
      </div>
    </div>


        <!-- ✅ Drilldown Modal -->
        <div v-if="drillOpen" class="modal-overlay" @click.self="closeDrilldown">
          <div class="modal panel">
            <div class="modal-header">
              <div class="modal-title">
                Drilldown: {{ drillRowLabel }}
              </div>
              <button class="modal-close" @click="closeDrilldown">✕</button>
            </div>

            <div class="small-muted" style="margin-top:6px;">
              Period: <b>{{ drillPeriod }}</b> •
              Date: <b>{{ drillParams.startDate }}</b> → <b>{{ drillParams.endDate }}</b> •
              CU: <b>{{ cu }}</b>
            </div>

            <div v-if="drillLoading" class="panel" style="margin-top:10px;">
              Loading drilldown...
            </div>

            <div v-else-if="drillError" class="panel error-box" style="margin-top:10px;">
              {{ drillError }}
            </div>

            <div v-else-if="drillData" style="margin-top:10px;">

              <!-- Top Vendors -->
              <div v-if="(drillData.top_vendors?.length || 0) > 0" class="panel" style="margin-bottom:10px;">
                <div class="warn-title">Top Vendors</div>
                <div class="table-scroll">
                  <table class="mini-table">
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th style="text-align:right;">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="v in drillData.top_vendors" :key="v.vendor_no">
                        <td>{{ v.vendor_name || v.vendor_no }}</td>
                        <td style="text-align:right;">{{ formatMoney(v.amount) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Top Customers -->
              <div v-if="(drillData.top_customers?.length || 0) > 0" class="panel" style="margin-bottom:10px;">
                <div class="warn-title">Top Customers</div>
                <div class="table-scroll">
                  <table class="mini-table">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th style="text-align:right;">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="c in drillData.top_customers" :key="c.customer_no">
                        <td>{{ c.customer_name || c.customer_no }}</td>
                        <td style="text-align:right;">{{ formatMoney(c.amount) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Transactions -->
              <div class="panel">
                <div class="warn-title">Transactions ({{ drillData.transactions?.length || 0 }})</div>

                <div class="table-scroll">
                  <table class="mini-table">
                    <thead>
                      <tr>
                        <th style="width:110px;">Date</th>
                        <th>Description</th>
                        <th style="width:120px;">Vendor</th>
                        <th style="width:120px;">Customer</th>
                        <th style="width:120px;">Invoice</th>
                        <th style="width:140px; text-align:right;">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="t in drillData.transactions" :key="t.id || (t.docnumber + '_' + t.datum)">
                        <td>{{ fmtDate(t.datum) }}</td>
                        <td>{{ t.oms25 || "" }}</td>
                        <td>{{ t.vendor_name || t.vendor_no || "" }}</td>
                        <td>{{ t.customer_name || t.customer_no || "" }}</td>
                        <td>{{ t.faktuurnr || t.docnumber || "" }}</td>
                        <td style="text-align:right;">{{ formatMoney(t.amount) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

              </div>
            </div>

          </div>
        </div>



  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "PnlReportPage",

  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");

    // default = current month
    const first = `${yyyy}-${mm}-01`;
    const last = this.lastDayOfMonth(yyyy, Number(mm));

    return {
        startDate: first,
        endDate: last,
        cu: "ALL",

        showPercent: true,
        showDetails: false,

        loading: false,
        error: null,
        mtdReport: null,
        ytdReport: null,
        lytdReport: null,

        drillOpen: false,
        drillLoading: false,
        drillError: null,
        drillData: null,
        drillRow: null,
        drillPeriod: "MTD",

        analysisOpen: false,
        pabloTakeOpen: false,
        focusedRowKey: null,
        focusTimer: null


    };
  },

  computed: {
    canRun() {
      return !!this.startDate && !!this.endDate && String(this.cu || "").length > 0;
    },

    reportLoaded() {
      return !!this.mtdReport && !!this.ytdReport && !!this.lytdReport;
    },

    monthObj() {
      const m = this.mtdReport?.months?.[0] || null;
      return this.collapseLinesForMonth(m);
    },

    displayMonthLabel() {
      const m = this.monthObj?.month_start;
      return m ? this.formatMonthLabelShort(m) : "";
    },

    ytdTotalsObj() {
      const summed = this.sumMonthsToPeriod(this.ytdReport?.months || []);
      return this.collapseLinesForMonth(summed);
    },


    lytdTotalsObj() {
      const summed = this.sumMonthsToPeriod(this.lytdReport?.months || []);
      return this.collapseLinesForMonth(summed);
    },


    currentYearLabel() {
      return String(this.endDate || "").slice(0, 4) || "TY";
    },

    priorYearLabel() {
      const y = Number(this.currentYearLabel);
      return Number.isFinite(y) ? String(y - 1) : "LY";
    },

    executiveMetrics() {
      if (!this.reportLoaded || !this.opexAnalysis?.ytd || !this.opexAnalysis?.lytd) return null;

      const ytd = this.opexAnalysis.ytd;
      const lytd = this.opexAnalysis.lytd;
      const salesChangePct = this.percentChange(ytd.revenue, lytd.revenue);
      const grossMarginYtd = this.percentOf(ytd.grossProfitAfter263A, ytd.revenue);
      const grossMarginLytd = this.percentOf(lytd.grossProfitAfter263A, lytd.revenue);
      const opProfitChangePct = this.percentChange(
        ytd.operatingProfitBeforeDepreciation,
        lytd.operatingProfitBeforeDepreciation
      );
      const opMarginYtd = this.percentOf(ytd.operatingProfitBeforeDepreciation, ytd.revenue);
      const opMarginLytd = this.percentOf(lytd.operatingProfitBeforeDepreciation, lytd.revenue);

      return {
        netSales: ytd.revenue,
        salesChangePct,
        grossMarginYtd,
        grossMarginLytd,
        grossMarginPointChange: grossMarginYtd == null || grossMarginLytd == null ? null : grossMarginYtd - grossMarginLytd,
        operatingProfit: ytd.operatingProfitBeforeDepreciation,
        opProfitChangePct,
        opMarginYtd,
        opMarginLytd,
        opMarginPointChange: opMarginYtd == null || opMarginLytd == null ? null : opMarginYtd - opMarginLytd
      };
    },

    executiveKpis() {
      const m = this.executiveMetrics;
      if (!m) return [];

      return [
        {
          key: "net_sales",
          label: "NET SALES",
          value: this.formatCompactMoney(m.netSales),
          comparison: `${this.formatSignedPercent(m.salesChangePct)} vs LYTD`,
          arrow: this.directionArrow(m.salesChangePct),
          tone: this.directionTone(m.salesChangePct),
          rowKey: "net_sales"
        },
        {
          key: "gross_margin",
          label: "GROSS MARGIN",
          value: this.formatPercent(m.grossMarginYtd),
          comparison: `${this.formatSignedPoints(m.grossMarginPointChange)} vs LYTD`,
          arrow: this.directionArrow(m.grossMarginPointChange),
          tone: this.directionTone(m.grossMarginPointChange),
          rowKey: "gross_profit_after_263a"
        },
        {
          key: "operating_profit",
          label: "OPERATING PROFIT",
          value: this.formatCompactMoney(m.operatingProfit),
          comparison: `${this.formatSignedPercent(m.opProfitChangePct)} vs LYTD`,
          arrow: this.directionArrow(m.opProfitChangePct),
          tone: this.directionTone(m.opProfitChangePct),
          rowKey: "operating_profit_before_depr"
        },
        {
          key: "operating_margin",
          label: "OPERATING MARGIN",
          value: this.formatPercent(m.opMarginYtd),
          comparison: `${this.formatSignedPoints(m.opMarginPointChange)} vs LYTD`,
          arrow: this.directionArrow(m.opMarginPointChange),
          tone: this.directionTone(m.opMarginPointChange),
          rowKey: "operating_profit_before_depr"
        }
      ];
    },

    salesTrendChart() {
      const ty = this.ytdReport?.months || [];
      const ly = this.lytdReport?.months || [];
      return this.buildLineChart(
        ty,
        ly,
        m => Number(m?.net_sales || 0),
        "money"
      );
    },

    grossMarginTrendChart() {
      const ty = this.ytdReport?.months || [];
      const ly = this.lytdReport?.months || [];
      return this.buildLineChart(
        ty,
        ly,
        m => this.percentOf(Number(m?.gross_profit_after_263a || 0), Number(m?.net_sales || 0)),
        "percent"
      );
    },

    cumulativeGrossMarginTrendChart() {
      const toCumulativeMarginMonths = (months) => {
        let cumulativeGrossProfit = 0;
        let cumulativeNetSales = 0;

        return (months || []).map(month => {
          cumulativeGrossProfit += Number(month?.gross_profit_after_263a || 0);
          cumulativeNetSales += Number(month?.net_sales || 0);

          return {
            ...month,
            _cumulative_gross_margin: this.percentOf(cumulativeGrossProfit, cumulativeNetSales)
          };
        });
      };

      const ty = toCumulativeMarginMonths(this.ytdReport?.months || []);
      const ly = toCumulativeMarginMonths(this.lytdReport?.months || []);

      return this.buildLineChart(
        ty,
        ly,
        month => month?._cumulative_gross_margin,
        "percent"
      );
    },

    auditWatch() {
      const empty = {
        items: [],
        scannedAccountCount: 0,
        baselineMonthCount: 0,
        sameMonthLyLabel: ""
      };

      if (!this.reportLoaded || !this.monthObj) return empty;

      const current = this.monthObj;
      const currentMonthStart = String(current.month_start || "");
      const currentMonthNo = currentMonthStart.slice(5, 7);
      const currentSales = Math.abs(Number(current.net_sales || 0));
      if (!currentMonthNo || currentSales <= 0) return empty;

      // Compare the selected/current month to every earlier month in the current YTD period.
      // We intentionally exclude the current month from the baseline so an anomaly does not dilute itself.
      const priorMonths = (this.ytdReport?.months || [])
        .filter(month => String(month?.month_start || "") < currentMonthStart)
        .map(month => this.collapseLinesForMonth(month));

      const sameMonthLyRaw = (this.lytdReport?.months || []).find(
        month => String(month?.month_start || "").slice(5, 7) === currentMonthNo
      ) || null;
      const sameMonthLy = sameMonthLyRaw ? this.collapseLinesForMonth(sameMonthLyRaw) : null;

      if (!priorMonths.length) {
        return {
          ...empty,
          sameMonthLyLabel: sameMonthLy?.month_start ? this.formatMonthLabelShort(sameMonthLy.month_start) : ""
        };
      }

      const lineMap = period => new Map(
        (period?.lines || []).map(line => [String(line.acct_no || "").trim(), line])
      );

      const currentMap = lineMap(current);
      const priorMaps = priorMonths.map(lineMap);
      const lyMap = lineMap(sameMonthLy);
      const accountNos = new Set(currentMap.keys());
      priorMaps.forEach(map => map.forEach((_, acct) => accountNos.add(acct)));
      lyMap.forEach((_, acct) => accountNos.add(acct));

      const priorSalesTotal = priorMonths.reduce(
        (sum, month) => sum + Math.abs(Number(month?.net_sales || 0)),
        0
      );
      const lySales = Math.abs(Number(sameMonthLy?.net_sales || 0));

      // Scales with the CU being viewed, but never chases tiny noise.
      const materialFloor = Math.max(10000, currentSales * 0.001);
      const results = [];

      // Audit Watch must mirror the scope of this P&L: stop at Operating Profit.
      // OTHER_INCOME, OTHER_EXPENSE and TAX live below that cutoff and must not be scanned.
      const allowedSections = new Set(["SALES", "COGS", "OPEX"]);
      let scannedAccountCount = 0;

      for (const acctNo of accountNos) {
        const currentLine = currentMap.get(acctNo) || null;
        const priorLines = priorMaps.map(map => map.get(acctNo) || null);
        const lyLine = lyMap.get(acctNo) || null;
        const representative = currentLine || priorLines.find(Boolean) || lyLine;
        if (!representative) continue;

        const section = String(representative.section || "").toUpperCase();
        if (!allowedSections.has(section)) continue;
        scannedAccountCount += 1;

        const currentAmount = Number(currentLine?.amount || 0);
        const priorAmounts = priorLines.map(line => Number(line?.amount || 0));
        const lyAmount = sameMonthLy ? Number(lyLine?.amount || 0) : null;

        // Ordinary positive sales accounts are usually mix/volume, not control exceptions.
        // Contra-sales (negative) remain in the scan because rebates/KDA/discounts can be posting issues.
        const priorSignedAverage = priorAmounts.reduce((sum, amount) => sum + amount, 0) / priorAmounts.length;
        if (section === "SALES" && currentAmount >= 0 && priorSignedAverage >= 0 && Number(lyAmount || 0) >= 0) continue;

        const currentMagnitude = Math.abs(currentAmount);
        const priorMagnitudes = priorAmounts.map(Math.abs);
        const priorAvgMagnitude = priorMagnitudes.reduce((sum, amount) => sum + amount, 0) / priorMagnitudes.length;
        const priorTotalMagnitude = priorMagnitudes.reduce((sum, amount) => sum + amount, 0);
        const sameMonthLyMagnitude = sameMonthLy ? Math.abs(Number(lyAmount || 0)) : null;

        const currentPct = currentSales > 0 ? (currentMagnitude / currentSales) * 100 : 0;
        const priorPct = priorSalesTotal > 0 ? (priorTotalMagnitude / priorSalesTotal) * 100 : 0;
        const lyPct = lySales > 0 && sameMonthLyMagnitude != null ? (sameMonthLyMagnitude / lySales) * 100 : null;
        const rateBps = (currentPct - priorPct) * 100;

        const avgChangePct = priorAvgMagnitude > 0.01
          ? ((currentMagnitude - priorAvgMagnitude) / priorAvgMagnitude) * 100
          : (currentMagnitude > 0 ? 999 : 0);
        const lyChangePct = sameMonthLyMagnitude != null && sameMonthLyMagnitude > 0.01
          ? ((currentMagnitude - sameMonthLyMagnitude) / sameMonthLyMagnitude) * 100
          : null;

        const deltaVsAvg = currentMagnitude - priorAvgMagnitude;
        const priorNonZeroCount = priorMagnitudes.filter(amount => amount >= materialFloor * 0.15).length;
        const signFlip = Math.abs(currentAmount) >= materialFloor && Math.abs(priorSignedAverage) >= materialFloor && currentAmount * priorSignedAverage < 0;
        const newActivity = currentMagnitude >= materialFloor * 1.5 && priorAvgMagnitude < materialFloor * 0.20;
        const missingOrVeryLow = priorAvgMagnitude >= materialFloor &&
          priorNonZeroCount >= Math.min(2, priorMonths.length) &&
          currentMagnitude <= priorAvgMagnitude * 0.25;
        const unusualVsAvg = Math.abs(avgChangePct) >= 60 && Math.max(currentMagnitude, priorAvgMagnitude) >= materialFloor;
        const unusualRate = Math.abs(rateBps) >= 25 && Math.abs(deltaVsAvg) >= materialFloor;
        const unusualVsLy = lyChangePct != null && Math.abs(lyChangePct) >= 75 &&
          Math.max(currentMagnitude, sameMonthLyMagnitude || 0) >= materialFloor;
        const extremeDollarMove = Math.abs(deltaVsAvg) >= materialFloor * 2 && Math.abs(avgChangePct) >= 100;

        // A routine volume swing should not create a review card by itself.
        const shouldFlag = signFlip || newActivity || missingOrVeryLow || extremeDollarMove ||
          (unusualVsAvg && (unusualRate || unusualVsLy));
        if (!shouldFlag) continue;

        const avgDirection = currentMagnitude >= priorAvgMagnitude ? "above" : "below";
        const avgPctAbs = Math.abs(avgChangePct);
        const parts = [];

        if (signFlip) {
          parts.push(`The account changed sign versus its prior-month YTD pattern.`);
        } else if (newActivity) {
          parts.push(`Current-month activity is ${this.formatCompactMoney(currentMagnitude)} after averaging only ${this.formatCompactMoney(priorAvgMagnitude)} across prior YTD months.`);
        } else if (missingOrVeryLow) {
          parts.push(`Current-month activity is ${this.formatCompactMoney(currentMagnitude)}, ${avgPctAbs.toFixed(0)}% below its prior-month YTD average of ${this.formatCompactMoney(priorAvgMagnitude)}.`);
        } else {
          parts.push(`Current-month activity is ${avgPctAbs.toFixed(0)}% ${avgDirection} its prior-month YTD average (${this.formatCompactMoney(currentMagnitude)} vs ${this.formatCompactMoney(priorAvgMagnitude)}).`);
        }

        if (sameMonthLyMagnitude != null && Math.max(currentMagnitude, sameMonthLyMagnitude) >= materialFloor * 0.5) {
          const lyDirection = currentMagnitude >= sameMonthLyMagnitude ? "above" : "below";
          parts.push(`${this.formatMonthLabelShort(sameMonthLy.month_start)} was ${this.formatCompactMoney(sameMonthLyMagnitude)} (${lyDirection} comparison check).`);
        }

        if (Math.abs(rateBps) >= 20) {
          parts.push(`Share of net sales is ${this.formatPercent(currentPct)} versus ${this.formatPercent(priorPct)} across prior YTD months (${this.formatBps(rateBps)}).`);
        }

        const dollarFactor = Math.min(8, Math.abs(deltaVsAvg) / Math.max(materialFloor, 1));
        const avgFactor = Math.min(6, Math.abs(avgChangePct) / 50);
        const rateFactor = Math.min(6, Math.abs(rateBps) / 25);
        const lyFactor = lyChangePct == null ? 0 : Math.min(4, Math.abs(lyChangePct) / 75);
        const score = dollarFactor + avgFactor + rateFactor + lyFactor + (signFlip ? 6 : 0) + (newActivity || missingOrVeryLow ? 3 : 0);
        const severity = signFlip || newActivity || missingOrVeryLow || Math.abs(rateBps) >= 75 || Math.abs(avgChangePct) >= 150
          ? "high"
          : "review";

        results.push({
          acctNo,
          label: representative.line_name || representative.acct_desc || acctNo,
          shortLabel: this.cleanAccountLabel(representative.line_name || representative.acct_desc || acctNo),
          section,
          currentAmount,
          currentMagnitude,
          priorAvgMagnitude,
          sameMonthLyMagnitude,
          currentPct,
          priorPct,
          lyPct,
          rateBps,
          avgChangePct,
          lyChangePct,
          deltaVsAvg,
          score,
          severity,
          text: parts.join(" ")
        });
      }

      const items = results
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);

      return {
        items,
        scannedAccountCount,
        baselineMonthCount: priorMonths.length,
        sameMonthLyLabel: sameMonthLy?.month_start ? this.formatMonthLabelShort(sameMonthLy.month_start) : ""
      };
    },

    opexMovers() {
      if (!this.reportLoaded || !this.ytdTotalsObj || !this.lytdTotalsObj) return [];

      const currentSales = Math.abs(Number(this.ytdTotalsObj.net_sales || 0));
      const priorSales = Math.abs(Number(this.lytdTotalsObj.net_sales || 0));
      const priorMap = new Map(
        (this.lytdTotalsObj.lines || []).map(line => [String(line.acct_no || "").trim(), line])
      );

      const rows = [];
      for (const line of (this.ytdTotalsObj.lines || [])) {
        const acctNo = String(line.acct_no || "").trim();
        if (!acctNo || line.section !== "OPEX" || line.subgroup === "CREDITS") continue;
        if (acctNo === "51400") continue; // depreciation is added back in the headline operating-profit metric

        const prior = priorMap.get(acctNo) || {};
        const currentAmount = Number(line.amount || 0);
        const priorAmount = Number(prior.amount || 0);
        const currentBurden = Math.abs(currentAmount);
        const priorBurden = Math.abs(priorAmount);
        const deltaBurden = currentBurden - priorBurden;
        const currentPct = currentSales > 0 ? (currentBurden / currentSales) * 100 : 0;
        const priorPct = priorSales > 0 ? (priorBurden / priorSales) * 100 : 0;
        const bpsChange = (currentPct - priorPct) * 100;

        rows.push({
          acctNo,
          label: line.line_name || line.acct_desc || acctNo,
          shortLabel: this.cleanAccountLabel(line.line_name || line.acct_desc || acctNo),
          currentAmount,
          priorAmount,
          currentBurden,
          priorBurden,
          deltaBurden,
          currentPct,
          priorPct,
          bpsChange
        });
      }

      const materialFloor = Math.max(5000, currentSales * 0.0001);
      return rows.filter(row => Math.abs(row.deltaBurden) >= materialFloor);
    },

    topOpexUnfavorable() {
      const rows = this.opexMovers
        .filter(row => row.deltaBurden > 0)
        .sort((a, b) => b.deltaBurden - a.deltaBurden)
        .slice(0, 4);
      return this.withMoverBars(rows);
    },

    topOpexFavorable() {
      const rows = this.opexMovers
        .filter(row => row.deltaBurden < 0)
        .sort((a, b) => a.deltaBurden - b.deltaBurden)
        .slice(0, 4);
      return this.withMoverBars(rows);
    },

    topGrossMarginDriver() {
      if (!this.reportLoaded || !this.ytdTotalsObj || !this.lytdTotalsObj) return null;

      const currentGross = Math.abs(Number(this.ytdTotalsObj.gross_sales || 0));
      const priorGross = Math.abs(Number(this.lytdTotalsObj.gross_sales || 0));
      const currentNet = Math.abs(Number(this.ytdTotalsObj.net_sales || 0));
      const priorNet = Math.abs(Number(this.lytdTotalsObj.net_sales || 0));
      const priorMap = new Map(
        (this.lytdTotalsObj.lines || []).map(line => [String(line.acct_no || "").trim(), line])
      );

      const candidates = [];
      for (const line of (this.ytdTotalsObj.lines || [])) {
        const acctNo = String(line.acct_no || "").trim();
        if (!acctNo || !["SALES", "COGS"].includes(line.section)) continue;

        const prior = priorMap.get(acctNo) || {};
        const currentAmount = Number(line.amount || 0);
        const priorAmount = Number(prior.amount || 0);

        // For sales, focus on contra lines (rebates/KDA/discounts), not ordinary product-mix revenue lines.
        if (line.section === "SALES" && currentAmount >= 0 && priorAmount >= 0) continue;

        const currentDenom = line.section === "SALES" ? currentGross : currentNet;
        const priorDenom = line.section === "SALES" ? priorGross : priorNet;
        if (currentDenom <= 0 || priorDenom <= 0) continue;

        const currentRate = (currentAmount / currentDenom) * 100;
        const priorRate = (priorAmount / priorDenom) * 100;
        const rateChangePts = currentRate - priorRate;
        const impact = (rateChangePts / 100) * currentDenom;

        candidates.push({
          acctNo,
          section: line.section,
          label: line.line_name || line.acct_desc || acctNo,
          currentAmount,
          priorAmount,
          currentRate,
          priorRate,
          rateChangePts,
          impact,
          baseLabel: line.section === "SALES" ? "gross sales" : "net sales"
        });
      }

      return candidates.sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact))[0] || null;
    },

    pabloTalkingPoints() {
      const m = this.executiveMetrics;
      if (!m) return [];

      const points = [];
      const salesDirection = m.salesChangePct >= 0 ? "up" : "down";
      points.push({
        key: "sales",
        rowKey: "net_sales",
        text: `Net sales are ${salesDirection} ${this.formatAbsPercent(m.salesChangePct)} YTD to ${this.formatCompactMoney(m.netSales)} versus ${this.formatCompactMoney(this.opexAnalysis.lytd.revenue)} last year.`
      });

      const gmDirection = m.grossMarginPointChange >= 0 ? "improved" : "declined";
      points.push({
        key: "gross-margin",
        rowKey: "gross_profit_after_263a",
        text: `Gross margin ${gmDirection} to ${this.formatPercent(m.grossMarginYtd)} from ${this.formatPercent(m.grossMarginLytd)}, a ${this.formatAbsPoints(m.grossMarginPointChange)}-point ${m.grossMarginPointChange >= 0 ? "gain" : "decline"}.`
      });

      const opDirection = m.opProfitChangePct >= 0 ? "increased" : "declined";
      points.push({
        key: "operating-profit",
        rowKey: "operating_profit_before_depr",
        text: `Operating profit before depreciation ${opDirection} ${this.formatAbsPercent(m.opProfitChangePct)} to ${this.formatCompactMoney(m.operatingProfit)}; operating margin is ${this.formatPercent(m.opMarginYtd)} versus ${this.formatPercent(m.opMarginLytd)} LYTD.`
      });

      const driver = this.topGrossMarginDriver;
      if (driver) {
        const direction = driver.impact >= 0 ? "favorable" : "unfavorable";
        points.push({
          key: `driver-${driver.acctNo}`,
          acctNo: driver.acctNo,
          label: driver.label,
          text: `${this.cleanAccountLabel(driver.label)} moved from ${this.formatPercent(Math.abs(driver.priorRate))} to ${this.formatPercent(Math.abs(driver.currentRate))} of ${driver.baseLabel}, a ${this.formatAbsPoints(driver.rateChangePts)}-point ${direction} shift.`
        });
      }

      for (const [idx, mover] of this.topOpexUnfavorable.slice(0, 2).entries()) {
        points.push({
          key: `opex-bad-${idx}-${mover.acctNo}`,
          acctNo: mover.acctNo,
          label: mover.label,
          text: `${mover.shortLabel} increased ${this.formatCompactMoney(mover.deltaBurden)} YTD to ${this.formatCompactMoney(mover.currentBurden)} and now represents ${this.formatPercent(mover.currentPct)} of net sales versus ${this.formatPercent(mover.priorPct)} LYTD.`
        });
      }

      const favorable = this.topOpexFavorable[0];
      if (favorable) {
        points.push({
          key: `opex-good-${favorable.acctNo}`,
          acctNo: favorable.acctNo,
          label: favorable.label,
          text: `${favorable.shortLabel} is a favorable comparison, down ${this.formatCompactMoney(Math.abs(favorable.deltaBurden))} YTD to ${this.formatCompactMoney(favorable.currentBurden)} from ${this.formatCompactMoney(favorable.priorBurden)} last year.`
        });
      }

      // Always return exactly seven concise points. Fill any gap with the next material OPEX movers.
      const usedAccounts = new Set(points.map(p => p.acctNo).filter(Boolean));
      const fallbackMovers = [...this.topOpexUnfavorable, ...this.topOpexFavorable]
        .filter(mover => !usedAccounts.has(mover.acctNo));

      for (const mover of fallbackMovers) {
        if (points.length >= 7) break;
        const favorableMove = mover.deltaBurden < 0;
        points.push({
          key: `fallback-${mover.acctNo}`,
          acctNo: mover.acctNo,
          label: mover.label,
          text: `${mover.shortLabel} ${favorableMove ? "improved" : "increased"} by ${this.formatCompactMoney(Math.abs(mover.deltaBurden))} versus LYTD, with expense burden ${favorableMove ? "falling" : "rising"} ${Math.abs(Math.round(Number(mover.bpsChange) || 0))} bps.`
        });
      }

      const summaryPool = [
        {
          key: "net-opex-summary",
          rowKey: "net_operating_expenses",
          text: `Net operating expense burden is ${this.formatPercent(this.percentOf(Math.abs(this.ytdTotalsObj.net_operating_expenses), Math.abs(this.ytdTotalsObj.net_sales)))} of net sales versus ${this.formatPercent(this.percentOf(Math.abs(this.lytdTotalsObj.net_operating_expenses), Math.abs(this.lytdTotalsObj.net_sales)))} LYTD.`
        },
        {
          key: "gross-profit-summary",
          rowKey: "gross_profit_after_263a",
          text: `Gross profit after 263A is ${this.formatCompactMoney(this.opexAnalysis.ytd.grossProfitAfter263A)} YTD versus ${this.formatCompactMoney(this.opexAnalysis.lytd.grossProfitAfter263A)} last year.`
        },
        {
          key: "fixed-opex-summary",
          rowKey: "operating_expenses",
          text: `Fixed OPEX burden is ${this.formatPercent(this.opexAnalysis.ytd.fixedOpexPctRevenue)} of net sales versus ${this.formatPercent(this.opexAnalysis.lytd.fixedOpexPctRevenue)} LYTD.`
        },
        {
          key: "variable-opex-summary",
          rowKey: "operating_expenses",
          text: `Variable OPEX burden is ${this.formatPercent(this.opexAnalysis.ytd.variableOpexPctRevenue)} of net sales versus ${this.formatPercent(this.opexAnalysis.lytd.variableOpexPctRevenue)} LYTD.`
        }
      ];

      for (const summary of summaryPool) {
        if (points.length >= 7) break;
        if (!points.some(point => point.key === summary.key)) points.push(summary);
      }

      return points.slice(0, 7);
    },

    collapsedGroups() {
      return [
        {
          key: "SALARIES_OFFICE",
          label: "SALARIES - OFFICE",
          targetAcct: "54900",
          accounts: ["54900", "55800", "55902", "55903", "56000"]
        }
      ];
    },

    drillRowLabel() {
      if (!this.drillRow) return "";
      // row.label includes "(acct)" already for details
      return this.drillRow.label || "";
    },

    drillParams() {
      const p = this.drillPeriod || "MTD";
      const y = String(this.startDate).slice(0, 4);

      if (p === "YTD") {
        return { startDate: `${y}-01-01`, endDate: this.endDate };
      }
      if (p === "LYTD") {
        const ly = String(Number(y) - 1);
        return { startDate: `${ly}-01-01`, endDate: `${ly}${String(this.endDate).slice(4)}` };
      }
      return { startDate: this.startDate, endDate: this.endDate }; // MTD
    },

  

    unmappedAccounts() {
      const s = new Set();
      (this.mtdReport?.unmapped_accounts || []).forEach(a => s.add(a));
      (this.ytdReport?.unmapped_accounts || []).forEach(a => s.add(a));
      (this.lytdReport?.unmapped_accounts || []).forEach(a => s.add(a));
      return Array.from(s).sort();
    },

    periodsForDetails() {
      const out = [];
      if (this.monthObj) out.push(this.monthObj);
      if (this.ytdTotalsObj) out.push(this.ytdTotalsObj);
      if (this.lytdTotalsObj) out.push(this.lytdTotalsObj);
      return out;
    },


    months() {
      return this.mtdReport?.months || [];
    },

    opexAnalysis() {
      if (!this.reportLoaded) return null;

      const mtd = this.buildOperatingAnalysis(this.monthObj);
      const ytd = this.buildOperatingAnalysis(this.ytdTotalsObj);
      const lytd = this.buildOperatingAnalysis(this.lytdTotalsObj);

      return {
        mtd,
        ytd,
        lytd,
        ytdVsLytd: this.compareOperatingAnalysis(ytd, lytd),

        // This is the old "DOL" number, renamed correctly.
        observedProfitSensitivityYtdVsLytd: this.calcObservedProfitSensitivity(ytd, lytd),

        // True DOL is unavailable until we have contribution margin from OE history.
        estimatedDolMtd: this.calcEstimatedDol(mtd),
        estimatedDolYtd: this.calcEstimatedDol(ytd),
        estimatedDolLytd: this.calcEstimatedDol(lytd)
      };
    },

    operatingProfitBridge() {
      if (!this.opexAnalysis?.ytd || !this.opexAnalysis?.lytd) return null;

      return this.buildOperatingProfitBridge(
        this.opexAnalysis.ytd,
        this.opexAnalysis.lytd
      );
    },


    // Fixed “Excel skeleton” rows (always)
    baseRows() {
      return [
        { key: "gross_sales", label: "Gross Sales", type: "month", bold: true, percentBase: "GROSS_SALES" },
        { key: "contra_sales", label: "Contra Sales", type: "month", percentBase: "GROSS_SALES" },
        { key: "net_sales", label: "Net Sales", type: "month", bold: true, doubleTop: true, percentBase: "GROSS_SALES" },

        { key: "cogs_hdr", label: "Cost of Goods Sold", type: "header" },
        { key: "base_cost_of_sales", label: "  Base Cost of Sales", type: "cogs", indent: true, percentBase: "GROSS_SALES" },
        { key: "variances_to_standard", label: "  Variances to Standard", type: "cogs", indent: true, percentBase: "GROSS_SALES" },
        { key: "direct_labor_variance", label: "  Direct Labor Variance", type: "cogs", indent: true, percentBase: "GROSS_SALES" },
        { key: "overhead_variance", label: "  Overhead Variance", type: "cogs", indent: true, percentBase: "GROSS_SALES" },
        { key: "total_cogs", label: "Total COGS", type: "month", bold: true, percentBase: "GROSS_SALES" },

        { key: "gross_profit", label: "Gross Profit", type: "month", bold: true, doubleTop: true, percentBase: "GROSS_SALES" },
        { key: "overhead_263a", label: "263A Overhead", type: "month", percentBase: "GROSS_SALES" },
        { key: "gross_profit_after_263a", label: "Gross Profit After 263A", type: "month", bold: true, doubleTop: true, percentBase: "GROSS_SALES" },

        { key: "operating_expenses", label: "Operating Expenses", type: "month", percentBase: "GROSS_SALES" },
        { key: "overhead_and_other_credits", label: "Overhead & Other Credits", type: "month", percentBase: "GROSS_SALES" },
        { key: "net_operating_expenses", label: "Net Operating Expenses", type: "month", bold: true, percentBase: "GROSS_SALES" },
        { key: "depr_addback", label: "Depreciation (Add-back)", type: "calc", indent: true, bold: false, percentBase: "NET_SALES" },
        { key: "operating_profit_before_depr", label: "Operating Profit/Loss (Before Depreciation)", type: "calc", bold: true, doubleTop: true, percentBase: "NET_SALES" },
        //{ key: "operating_profit", label: "Operating Profit/Loss", type: "calc", bold: true, doubleTop: true, percentBase: "NET_SALES" },


        //{ key: "other_income", label: "Other Income", type: "month", percentBase: "GROSS_SALES" },
        //{ key: "other_expense", label: "Other Expense", type: "month", percentBase: "GROSS_SALES" },
        //{ key: "net_income_before_taxes", label: "Net Income Before Taxes", type: "month", bold: true, doubleTop: true, percentBase: "GROSS_SALES" },
        //{ key: "taxes", label: "Taxes", type: "month", percentBase: "GROSS_SALES" },
        //{ key: "net_income", label: "Net Income", type: "month", bold: true, doubleTop: true, percentBase: "GROSS_SALES" }
      ];
    },

    // Build a stable set of detail rows across all months by account
    detailRows() {
      if (!this.showDetails) return [];

      const wantSections = new Set(["SALES", "COGS", "OPEX", "OTHER_INCOME", "OTHER_EXPENSE", "TAX"]);
      const bySection = new Map();

      for (const m of this.periodsForDetails) {
        for (const ln of (m.lines || [])) {
          if (!wantSections.has(ln.section)) continue;
          const sec = ln.section;

          if (!bySection.has(sec)) bySection.set(sec, new Map());

          const secMap = bySection.get(sec);
          const acct = (ln?.acct_no == null) ? "" : `${ln.acct_no}`.trim();
          if (!acct) continue;

          // ✅ skip only the non-primary members (keep primary acct so SALARIES - OFFICE still shows)
          if (this.isCollapsedMemberAcct(acct) && !this.isCollapsedPrimaryAcct(acct)) continue;



          if (!secMap.has(acct)) {
            secMap.set(acct, {
              key: `DETAIL:${sec}:${acct}`,
              label: `${ln.line_name || ln.acct_desc || acct} (${acct})`,
              type: "detail",
              section: sec,
              acct_no: acct,
              sort_order: Number(ln.sort_order ?? 999999),
              indent: true
            });
          }

        }
      }

      // Convert to sorted arrays, keep section order consistent
      const sectionOrder = ["SALES", "COGS", "OPEX", "OTHER_INCOME", "OTHER_EXPENSE", "TAX"];
      const out = [];

      for (const sec of sectionOrder) {
        const secMap = bySection.get(sec);
        if (!secMap) continue;

        const rows = Array.from(secMap.values()).sort((a, b) => {
          if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
          return a.acct_no.localeCompare(b.acct_no);
        });

        out.push({ key: `HDR:${sec}`, label: this.sectionLabel(sec), type: "subheader" });
        out.push(...rows);
      }

      return out;
    },

    // Inject details under the matching skeleton areas
    renderedRows() {
      const base = [...this.baseRows];

      if (!this.showDetails) {
        const stopKey = "operating_profit_before_depr";
        const idx = base.findIndex(r => r.key === stopKey);
        return idx >= 0 ? base.slice(0, idx + 1) : base;
      }


      // Where to inject each section’s detail block
      const injectAfterKey = {
        // sales details after Gross Sales block
        gross_sales: "SALES",
        // cogs details after COGS header
        cogs_hdr: "COGS",
        // opex after operating expenses line
        operating_expenses: "OPEX",
        // other after other income line
        other_income: "OTHER_INCOME",
        other_expense: "OTHER_EXPENSE",
        taxes: "TAX"
      };

      // Build lookup for detail blocks by section
      const blocks = new Map();
      for (const r of this.detailRows) {
        if (r.type === "subheader") {
          const sec = r.key.replace("HDR:", "");
          blocks.set(sec, []);
        } else if (r.type === "detail") {
          if (!blocks.has(r.section)) blocks.set(r.section, []);
          blocks.get(r.section).push(r);
        }
      }

      const out = [];
      for (const row of base) {
        out.push(row);

        const sec = injectAfterKey[row.key];
        if (sec && blocks.has(sec)) {
          // Add subheader then details (subheader only if there are rows)
          const details = blocks.get(sec) || [];
          if (details.length > 0) {
            out.push({ key: `SUBHDR:${sec}:${row.key}`, label: `${this.sectionLabel(sec)} Details`, type: "subheader", indent: true });
            out.push(...details);
          }
        }
      }

      // ✅ STOP after Operating Profit/Loss
      const stopKey = "operating_profit";
      const idx = out.findIndex(r => r.key === stopKey);
      return idx >= 0 ? out.slice(0, idx + 1) : out;

    }
  },

  methods: {
  openPabloTake() {
    if (!this.reportLoaded) return;
    this.pabloTakeOpen = true;
  },

  closePabloTake() {
    this.pabloTakeOpen = false;
  },

  handleExecutiveKpi(card) {
    if (!card?.rowKey) return;
    this.pabloTakeOpen = false;
    this.$nextTick(() => this.focusPnlRow(card.rowKey));
  },

  handleTalkingPoint(point) {
    if (!point) return;

    if (point.acctNo) {
      this.pabloTakeOpen = false;
      this.$nextTick(() => {
        this.openDrilldown(
          {
            type: "detail",
            acct_no: point.acctNo,
            label: point.label || point.acctNo
          },
          "YTD"
        );
      });
      return;
    }

    if (point.rowKey) {
      this.pabloTakeOpen = false;
      this.$nextTick(() => this.focusPnlRow(point.rowKey));
    }
  },

  openMoverDrilldown(mover) {
    if (!mover?.acctNo) return;
    this.pabloTakeOpen = false;
    this.$nextTick(() => {
      this.openDrilldown(
        {
          type: "detail",
          acct_no: mover.acctNo,
          label: mover.label || mover.shortLabel || mover.acctNo
        },
        "YTD"
      );
    });
  },

  openAuditDrilldown(item) {
    if (!item?.acctNo) return;
    this.pabloTakeOpen = false;
    this.$nextTick(() => {
      this.openDrilldown(
        {
          type: "detail",
          acct_no: item.acctNo,
          label: item.label || item.shortLabel || item.acctNo
        },
        "MTD"
      );
    });
  },

  focusPnlRow(rowKey) {
    if (!rowKey) return;
    this.focusedRowKey = rowKey;

    this.$nextTick(() => {
      const el = document.querySelector(`[data-pnl-row="${rowKey}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    if (this.focusTimer) clearTimeout(this.focusTimer);
    this.focusTimer = setTimeout(() => {
      this.focusedRowKey = null;
      this.focusTimer = null;
    }, 2200);
  },

  percentOf(numerator, denominator) {
    const n = Number(numerator);
    const d = Number(denominator);
    if (!isFinite(n) || !isFinite(d) || Math.abs(d) < 0.0001) return null;
    return (n / d) * 100;
  },

  percentChange(current, prior) {
    const c = Number(current);
    const p = Number(prior);
    if (!isFinite(c) || !isFinite(p) || Math.abs(p) < 0.0001) return null;
    return ((c - p) / Math.abs(p)) * 100;
  },

  formatCompactMoney(v) {
    const n = Number(v);
    if (!isFinite(n)) return "-";
    const abs = Math.abs(n);
    let value;
    if (abs >= 1_000_000_000) value = `$${(abs / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 1 : 2)}B`;
    else if (abs >= 1_000_000) value = `$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2)}M`;
    else if (abs >= 1_000) value = `$${(abs / 1_000).toFixed(abs >= 100_000 ? 0 : 1)}K`;
    else value = `$${abs.toFixed(0)}`;
    return n < 0 ? `(${value})` : value;
  },

  formatSignedPercent(v) {
    const n = Number(v);
    if (!isFinite(n)) return "-";
    const sign = n > 0 ? "+" : "";
    return `${sign}${n.toFixed(1)}%`;
  },

  formatAbsPercent(v) {
    const n = Math.abs(Number(v));
    return isFinite(n) ? `${n.toFixed(1)}%` : "-";
  },

  formatSignedPoints(v) {
    const n = Number(v);
    if (!isFinite(n)) return "-";
    const sign = n > 0 ? "+" : "";
    return `${sign}${n.toFixed(2)} pts`;
  },

  formatAbsPoints(v) {
    const n = Math.abs(Number(v));
    return isFinite(n) ? n.toFixed(2) : "-";
  },

  formatBps(v) {
    const n = Number(v);
    if (!isFinite(n)) return "-";
    const rounded = Math.round(n);
    const sign = rounded > 0 ? "+" : "";
    return `${sign}${rounded} bps`;
  },

  directionArrow(v) {
    const n = Number(v);
    if (!isFinite(n) || Math.abs(n) < 0.0001) return "•";
    return n > 0 ? "▲" : "▼";
  },

  directionTone(v) {
    const n = Number(v);
    if (!isFinite(n) || Math.abs(n) < 0.0001) return "neutral";
    return n > 0 ? "good" : "bad";
  },

  cleanAccountLabel(label) {
    return String(label || "")
      .replace(/\s*\(\d+\)\s*$/, "")
      .replace(/\s+/g, " ")
      .trim();
  },

  withMoverBars(rows) {
    const max = Math.max(1, ...rows.map(row => Math.abs(Number(row.deltaBurden || 0))));
    return rows.map(row => ({
      ...row,
      absDelta: Math.abs(Number(row.deltaBurden || 0)),
      barPct: Math.max(8, Math.min(100, (Math.abs(Number(row.deltaBurden || 0)) / max) * 100))
    }));
  },

  buildLineChart(tyMonths, lyMonths, extractor, mode) {
    const width = 680;
    const height = 220;
    const left = 52;
    const right = 20;
    const top = 18;
    const bottom = 34;
    const plotWidth = width - left - right;
    const plotHeight = height - top - bottom;

    const ty = (tyMonths || []).map((m, index) => ({ month: m, index, value: Number(extractor(m)) }));
    const ly = (lyMonths || []).map((m, index) => ({ month: m, index, value: Number(extractor(m)) }));
    const finiteValues = [...ty, ...ly].map(p => p.value).filter(Number.isFinite);

    if (!finiteValues.length) {
      return { tyPoints: "", lyPoints: "", tyDots: [], lyDots: [], gridLines: [], xLabels: [] };
    }

    let min = Math.min(...finiteValues);
    let max = Math.max(...finiteValues);
    const rawRange = max - min;
    const pad = rawRange > 0 ? rawRange * 0.12 : Math.max(Math.abs(max) * 0.05, 1);
    min -= pad;
    max += pad;

    if (mode === "percent") {
      min = Math.max(0, min);
    }

    const range = Math.max(max - min, 0.0001);
    const count = Math.max(ty.length, ly.length, 1);
    const xFor = index => left + (count <= 1 ? plotWidth / 2 : (index / (count - 1)) * plotWidth);
    const yFor = value => top + ((max - value) / range) * plotHeight;
    const tooltipValue = value => mode === "money" ? this.formatCompactMoney(value) : this.formatPercent(value);

    const toDots = (arr, yearLabel) => arr
      .filter(p => Number.isFinite(p.value))
      .map(p => ({
        index: p.index,
        x: xFor(p.index),
        y: yFor(p.value),
        value: p.value,
        tooltip: `${this.formatMonthLabelShort(p.month?.month_start)} ${yearLabel}: ${tooltipValue(p.value)}`
      }));

    const tyDots = toDots(ty, this.currentYearLabel);
    const lyDots = toDots(ly, this.priorYearLabel);
    const pointsString = dots => dots.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

    const gridLines = Array.from({ length: 4 }, (_, index) => {
      const ratio = index / 3;
      const value = max - ratio * range;
      return {
        y: top + ratio * plotHeight,
        value,
        label: mode === "money" ? this.formatCompactMoney(value) : `${value.toFixed(1)}%`
      };
    });

    const xLabels = ty.map((p, index) => ({
      index,
      x: xFor(index),
      label: this.monthAbbrev(p.month?.month_start)
    }));

    return {
      tyPoints: pointsString(tyDots),
      lyPoints: pointsString(lyDots),
      tyDots,
      lyDots,
      tyLast: tyDots.length ? tyDots[tyDots.length - 1] : null,
      lyLast: lyDots.length ? lyDots[lyDots.length - 1] : null,
      gridLines,
      xLabels
    };
  },

  monthAbbrev(iso) {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleString("en-US", { month: "short" });
  },

  lastDayOfMonth(y, m) {
    const d = new Date(y, m, 0);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  },


    sectionLabel(sec) {
      if (sec === "SALES") return "Sales";
      if (sec === "COGS") return "COGS";
      if (sec === "OPEX") return "Operating Expenses";
      if (sec === "OTHER_INCOME") return "Other Income";
      if (sec === "OTHER_EXPENSE") return "Other Expense";
      if (sec === "TAX") return "Taxes";
      return sec;
    },

    async runReport() {
      if (this.loading) return;
      this.error = null;
      this.mtdReport = null;
      this.ytdReport = null;
      this.lytdReport = null;

      if (!this.canRun) return;

      this.loading = true;

      try {
        // MTD
        const mtdParams = { startDate: this.startDate, endDate: this.endDate, cu: this.cu };

        // YTD (Jan 1 → endDate)
        const y = String(this.startDate).slice(0, 4);
        const ytdParams = { startDate: `${y}-01-01`, endDate: this.endDate, cu: this.cu };

        // LYTD (prior year Jan 1 → prior year same end date)
        const ly = String(Number(y) - 1);
        const lytdParams = {
          startDate: `${ly}-01-01`,
          endDate: `${ly}${String(this.endDate).slice(4)}`, // keep -MM-DD
          cu: this.cu
        };

        // instead of Promise.all([...])
        const mtdRes = await axios.get("/pnlReport", { params: mtdParams });
        this.mtdReport = mtdRes.data;

        const ytdRes = await axios.get("/pnlReport", { params: ytdParams });
        this.ytdReport = ytdRes.data;

        const lytdRes = await axios.get("/pnlReport", { params: lytdParams });
        this.lytdReport = lytdRes.data;

      } catch (err) {
        console.error("runReport error:", err);
        this.error =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load P&L report.";
      } finally {
        this.loading = false;
      }
    },

    formatMonthLabel(iso) {
      // iso like "2025-09-01"
      if (!iso) return "";
      const d = new Date(`${iso}T00:00:00`);
      const mo = d.toLocaleString("en-US", { month: "short" });
      const yr = d.getFullYear();
      return `${mo} ${yr}`;
    },

    formatMonthLabelShort(iso) {
      if (!iso) return "";
      const d = new Date(`${iso}T00:00:00`);
      const mo = d.toLocaleString("en-US", { month: "short" });
      const yy = String(d.getFullYear()).slice(-2);
      return `${mo}-${yy}`;
    },


    formatMoney(v) {
      const n = Number(v);
      if (!isFinite(n)) return "";
      const abs = Math.abs(n);
      const formatted = abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return n < 0 ? `(${formatted})` : formatted;
    },

    formatPercent(v) {
      const n = Number(v);
      if (!isFinite(n)) return "";
      return `${n.toFixed(2)}%`;
    },

    // Return amount for this row+month (or null for header rows)
    cellAmount(row, month) {
      if (!row || !month) return null;

      if (row.type === "header" || row.type === "subheader") return null;

      if (row.type === "month") {
        const val = month[row.key];
        return typeof val === "number" ? val : (val == null ? 0 : Number(val));
      }

      if (row.type === "cogs") {
        const val = month?.cogs_breakdown?.[row.key];
        return typeof val === "number" ? val : (val == null ? 0 : Number(val));
      }

      if (row.type === "detail") {
        const acct = String(row.acct_no || "").trim();
        const ln = (month.lines || []).find(x => String(x.acct_no || "").trim() === acct);
        if (!ln) return 0;
        return typeof ln.amount === "number" ? ln.amount : Number(ln.amount || 0);
      }

      if (row.type === "calc") {
        // Depreciation account (from detail lines)
        const depLine = (month.lines || []).find(x => String(x.acct_no || "").trim() === "51400");
        const depAmt = Number(depLine?.amount || 0); // this will be NEGATIVE as an expense

        if (row.key === "depr_addback") {
          // show positive add-back
          return -depAmt;
        }

        if (row.key === "operating_profit_before_depr") {
          const gp = Number(month?.gross_profit_after_263a || 0);
          const noe = Number(month?.net_operating_expenses || 0);

          // add depreciation back (i.e., remove it from NOE)
          return (gp + noe) - depAmt; // depAmt is negative, so subtracting it increases profit
        }

        return 0;
      }




      return null;
    },

    // Percent for this row+month (null if not applicable)
    cellPercent(row, period) {
      if (!this.showPercent) return null;
      if (!row || !period) return null;

      if (row.type === "header" || row.type === "subheader") return null;

      // detail rows: compute ourselves to support net sales base
      let amt = Number(this.cellAmount(row, period) || 0);

      // choose denominator
      const gross = Number(period.gross_sales || 0);
      const net = Number(period.net_sales || 0);

      const isSalesRow =
        row.key === "gross_sales" ||
        row.key === "contra_sales" ||
        row.key === "net_sales" ||
        (row.type === "detail" && row.section === "SALES");

      const denom = isSalesRow ? gross : net;

      if (!isFinite(denom) || Math.abs(denom) < 0.0001) return null;
      return (amt / denom) * 100;
    },

    rowClass(row) {
      if (!row) return "";
      return {
        "row-header": row.type === "header",
        "row-subheader": row.type === "subheader",
        "row-bold": !!row.bold,
        "row-double-top": !!row.doubleTop
      };
    },

    isCollapsedPrimaryAcct(acctNoLike) {
      let acct = acctNoLike;
      if (acct && typeof acct === "object") {
        acct = acct.acct_no ?? acct.acctNo ?? acct.account ?? "";
      }
      const a = (acct == null) ? "" : `${acct}`.trim();
      if (!a) return false;

      // primary = first acct in the group accounts array
      return this.collapsedGroups.some(g => String(g.accounts?.[0] || "").trim() === a);
    },


    isCollapsedMemberAcct(acctNoLike) {
      let acct = acctNoLike;

      // if they accidentally pass the whole line object
      if (acct && typeof acct === "object") {
        acct = acct.acct_no ?? acct.acctNo ?? acct.account ?? "";
      }

      const a = (acct == null) ? "" : `${acct}`.trim();
      if (!a) return false;

      return this.collapsedGroups.some(g => g.accounts.includes(a));
    },

    collapseLinesForMonth(month) {
      if (!month || !Array.isArray(month.lines)) return month;

      const out = { ...month, lines: [...month.lines] };

      for (const grp of this.collapsedGroups) {
        const set = new Set(grp.accounts);

        const members = out.lines.filter(l => set.has(String(l.acct_no || "").trim()));
        if (!members.length) continue;

        // remove members
        out.lines = out.lines.filter(l => !set.has(String(l.acct_no || "").trim()));

        // pick a “primary” acct to represent the group in display (use first in grp.accounts)
        const primaryAcct = grp.accounts[0];
        const primary = members.find(x => String(x.acct_no || "").trim() === primaryAcct) || members[0];

        const sumAmt = members.reduce((s, x) => s + Number(x.amount || 0), 0);
        const sumRaw = members.reduce((s, x) => s + Number(x.raw_amount || 0), 0);

        const collapsed = {
          ...primary,
          // keep acct_no real-ish (so anything downstream that keys off acct_no still works)
          acct_no: primaryAcct,
          acct_desc: grp.label,
          line_name: grp.label,

          // stable identity + drilldown
          _groupKey: grp.key,
          _isCollapsedGroup: true,
          _components: members,

          amount: sumAmt,
          raw_amount: sumRaw,

          // let frontend recompute percent using your rules
          percent_of_gross_sales: null,

          // keep sort stable (use smallest sort among members or a hardcoded number)
          sort_order: Math.min(...members.map(x => Number(x.sort_order ?? 999999)))
        };

        out.lines.push(collapsed);
      }

      out.lines.sort((a, b) => {
        const sa = String(a.section || "").localeCompare(String(b.section || ""));
        if (sa !== 0) return sa;
        const so = Number(a.sort_order ?? 999999) - Number(b.sort_order ?? 999999);
        if (so !== 0) return so;
        return String(a.acct_no || "").localeCompare(String(b.acct_no || ""));
      });

      return out;
    },

    sumMonthsToPeriod(months) {
      if (!months || !months.length) return null;

      const sum = (arr, fn) => arr.reduce((s, x) => s + (Number(fn(x)) || 0), 0);

      const out = {
        // month_start is just a label placeholder for the UI
        month_start: months[months.length - 1].month_start,

        gross_sales: sum(months, m => m.gross_sales),
        contra_sales: sum(months, m => m.contra_sales),
        net_sales: sum(months, m => m.net_sales),

        total_cogs: sum(months, m => m.total_cogs),
        gross_profit: sum(months, m => m.gross_profit),
        overhead_263a: sum(months, m => m.overhead_263a),
        gross_profit_after_263a: sum(months, m => m.gross_profit_after_263a),

        operating_expenses: sum(months, m => m.operating_expenses),
        overhead_and_other_credits: sum(months, m => m.overhead_and_other_credits),
        net_operating_expenses: sum(months, m => m.net_operating_expenses),

        other_income: sum(months, m => m.other_income),
        other_expense: sum(months, m => m.other_expense),
        net_income_before_taxes: sum(months, m => m.net_income_before_taxes),
        taxes: sum(months, m => m.taxes),
        net_income: sum(months, m => m.net_income),

        cogs_breakdown: {
          base_cost_of_sales: sum(months, m => m.cogs_breakdown?.base_cost_of_sales),
          variances_to_standard: sum(months, m => m.cogs_breakdown?.variances_to_standard),
          total_actual_labor: sum(months, m => m.cogs_breakdown?.total_actual_labor),
          total_standard_labor_absorbed: sum(months, m => m.cogs_breakdown?.total_standard_labor_absorbed),
          direct_labor_variance: sum(months, m => m.cogs_breakdown?.direct_labor_variance),
          overhead: sum(months, m => m.cogs_breakdown?.overhead),
          total_overhead_absorbed: sum(months, m => m.cogs_breakdown?.total_overhead_absorbed),
          overhead_variance: sum(months, m => m.cogs_breakdown?.overhead_variance),
          total_cogs: sum(months, m => m.cogs_breakdown?.total_cogs)
        },

        // detail rows (account lines): aggregate by acct_no
        lines: []
      };

      // Merge detail lines by acct_no
      const lineMap = new Map();
      for (const m of months) {
        for (const ln of (m.lines || [])) {
          const acct = String(ln.acct_no || "").trim();
          if (!acct) continue;

          if (!lineMap.has(acct)) {
            lineMap.set(acct, { ...ln, amount: 0, raw_amount: 0 });
          }
          const cur = lineMap.get(acct);
          cur.amount += Number(ln.amount) || 0;
          cur.raw_amount += Number(ln.raw_amount) || 0;
        }
      }

      out.lines = Array.from(lineMap.values());

      return out;
    },

    isClickable(row) {
      // only detail rows (real GL accounts) for now
      return row && row.type === "detail" && String(row.acct_no || "").trim().length > 0;
    },

    async openDrilldown(row, period) {
      if (!this.isClickable(row)) return;

      this.drillOpen = true;
      this.drillLoading = true;
      this.drillError = null;
      this.drillData = null;
      this.drillRow = row;
      this.drillPeriod = period || "MTD";

      try {
        const acct = String(row.acct_no || "").trim();
        const { startDate, endDate } = this.drillParams;

        const { data } = await axios.get("/pnlDrilldown", {
          params: { acct, startDate, endDate, cu: this.cu, limit: 200 }
        });

        this.drillData = data;
      } catch (err) {
        console.error("openDrilldown error:", err);
        this.drillError = err?.response?.data?.error || err?.response?.data?.details || "Failed to load drilldown.";
      } finally {
        this.drillLoading = false;
      }
    },

    closeDrilldown() {
      this.drillOpen = false;
      this.drillLoading = false;
      this.drillError = null;
      this.drillData = null;
      this.drillRow = null;
      this.drillPeriod = "MTD";
    },

    fmtDate(v) {
      if (!v) return "";
      // works if backend returns Date or string
      const d = new Date(v);
      if (isNaN(d.getTime())) return String(v);
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    },

    printPNL() {
      if (!this.reportLoaded) {
        this.error = "Run the report before printing.";
        return;
      }

      const payload = {
        meta: {
          generatedAt: new Date().toISOString(),
          cu: this.cu,
          startDate: this.startDate,
          endDate: this.endDate,
          showPercent: this.showPercent,
          showDetails: this.showDetails,
          displayMonthLabel: this.displayMonthLabel
        },

        ranges: {
          mtd: {
            startDate: this.mtdReport?.startDate || this.startDate,
            endDate: this.mtdReport?.endDate || this.endDate
          },
          ytd: {
            startDate: this.ytdReport?.startDate || `${String(this.startDate).slice(0, 4)}-01-01`,
            endDate: this.ytdReport?.endDate || this.endDate
          },
          lytd: {
            startDate: this.lytdReport?.startDate || "",
            endDate: this.lytdReport?.endDate || ""
          }
        },

        rows: this.renderedRows.map(row => ({
          key: row.key,
          label: row.label,
          type: row.type,
          indent: !!row.indent,
          bold: !!row.bold,
          doubleTop: !!row.doubleTop,

          mtdAmount: this.cellAmount(row, this.monthObj),
          mtdPercent: this.showPercent ? this.cellPercent(row, this.monthObj) : null,

          ytdAmount: this.cellAmount(row, this.ytdTotalsObj),
          ytdPercent: this.showPercent ? this.cellPercent(row, this.ytdTotalsObj) : null,

          lytdAmount: this.cellAmount(row, this.lytdTotalsObj),
          lytdPercent: this.showPercent ? this.cellPercent(row, this.lytdTotalsObj) : null
        })),

        unmappedAccounts: this.unmappedAccounts || []
      };

      sessionStorage.setItem("pablo_pnl_print_payload", JSON.stringify(payload));

      const routeData = this.$router.resolve({
        path: "/print-pnl-report"
      });

      window.open(routeData.href, "_blank");
    },

    openAnalysis() {
      if (!this.reportLoaded) return;
      this.analysisOpen = true;
    },

    closeAnalysis() {
      this.analysisOpen = false;
    },

    safeDiv(numerator, denominator) {
      const n = Number(numerator);
      const d = Number(denominator);
      if (!isFinite(n) || !isFinite(d) || Math.abs(d) < 0.0001) return null;
      return n / d;
    },

    formatRatio(v) {
      const n = Number(v);
      if (!isFinite(n)) return "-";
      return n.toFixed(2);
    },

    formatPercentPoint(v) {
      const n = Number(v);
      if (!isFinite(n)) return "-";
      const sign = n > 0 ? "+" : "";
      return `${sign}${n.toFixed(2)} pts`;
    },

    formatChangePercent(v) {
      const n = Number(v);
      if (!isFinite(n)) return "-";
      const sign = n > 0 ? "+" : "";
      return `${sign}${n.toFixed(2)}%`;
    },

    buildOperatingAnalysis(period) {
      if (!period) return null;

      const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

      const lines = period.lines || [];

      const netSales = Number(period.net_sales || 0);
      const grossProfitAfter263A = Number(period.gross_profit_after_263a || 0);
      const netOperatingExpenses = Number(period.net_operating_expenses || 0);

      // Current frontend logic treats depreciation acct 51400 as an add-back.
      const depLine = lines.find(x => String(x.acct_no || "").trim() === "51400");
      const depreciationAmount = Number(depLine?.amount || 0);

      const operatingProfitBeforeDepreciation = round2(
        (grossProfitAfter263A + netOperatingExpenses) - depreciationAmount
      );

      const opexLines = lines.filter(x =>
        x.section === "OPEX" &&
        x.subgroup !== "CREDITS"
      );

      const fixedLines = opexLines.filter(x => String(x.type || "").toLowerCase() === "f");
      const variableLines = opexLines.filter(x => String(x.type || "").toLowerCase() === "v");
      const unclassifiedLines = opexLines.filter(x => {
        const t = String(x.type || "").toLowerCase();
        return t !== "f" && t !== "v";
      });

      const sum = (arr) => round2(arr.reduce((s, x) => s + Number(x.amount || 0), 0));

      // These are signed amounts. Expenses are usually negative in your display model.
      const fixedOpex = sum(fixedLines);
      const variableOpex = sum(variableLines);
      const unclassifiedOpex = sum(unclassifiedLines);
      const classifiedOpex = round2(fixedOpex + variableOpex);
      const totalOpex = round2(classifiedOpex + unclassifiedOpex);

      const pctOfRevenue = (amount) => {
        if (Math.abs(netSales) < 0.0001) return null;

        // Use absolute burden for analysis, so expenses show as positive % of revenue.
        return round2((Math.abs(Number(amount || 0)) / Math.abs(netSales)) * 100);
      };

      return {
        revenue: round2(netSales),

        grossProfitAfter263A: round2(grossProfitAfter263A),
        netOperatingExpenses: round2(netOperatingExpenses),
        depreciationAmount: round2(depreciationAmount),
        depreciationAddback: round2(-depreciationAmount),
        operatingProfitBeforeDepreciation,

        fixedOpex,
        variableOpex,
        classifiedOpex,
        unclassifiedOpex,
        totalOpex,

        fixedOpexBurden: round2(Math.abs(fixedOpex)),
        variableOpexBurden: round2(Math.abs(variableOpex)),
        classifiedOpexBurden: round2(Math.abs(classifiedOpex)),
        unclassifiedOpexBurden: round2(Math.abs(unclassifiedOpex)),
        totalOpexBurden: round2(Math.abs(totalOpex)),

        fixedOpexPctRevenue: pctOfRevenue(fixedOpex),
        variableOpexPctRevenue: pctOfRevenue(variableOpex),
        classifiedOpexPctRevenue: pctOfRevenue(classifiedOpex),
        unclassifiedOpexPctRevenue: pctOfRevenue(unclassifiedOpex),
        totalOpexPctRevenue: pctOfRevenue(totalOpex),
   
        fixedAccountCount: fixedLines.length,
        variableAccountCount: variableLines.length,
        unclassifiedAccountCount: unclassifiedLines.length,

        fixedLines,
        variableLines,
        unclassifiedLines
      };
    },

    compareOperatingAnalysis(current, prior) {
      if (!current || !prior) return null;

      const changePct = (cur, prev) => {
        const div = this.safeDiv(Number(cur) - Number(prev), Math.abs(Number(prev)));
        return div == null ? null : div * 100;
      };

      const pointChange = (cur, prev) => {
        if (cur == null || prev == null) return null;
        return Number(cur) - Number(prev);
      };

      return {
        revenueChangePct: changePct(current.revenue, prior.revenue),

        fixedOpexChangePct: changePct(current.fixedOpexBurden, prior.fixedOpexBurden),
        variableOpexChangePct: changePct(current.variableOpexBurden, prior.variableOpexBurden),
        classifiedOpexChangePct: changePct(current.classifiedOpexBurden, prior.classifiedOpexBurden),
        unclassifiedOpexChangePct: changePct(current.unclassifiedOpexBurden, prior.unclassifiedOpexBurden),
        totalOpexChangePct: changePct(current.totalOpexBurden, prior.totalOpexBurden),

        fixedOpexPctPointChange: pointChange(current.fixedOpexPctRevenue, prior.fixedOpexPctRevenue),
        variableOpexPctPointChange: pointChange(current.variableOpexPctRevenue, prior.variableOpexPctRevenue),
        classifiedOpexPctPointChange: pointChange(current.classifiedOpexPctRevenue, prior.classifiedOpexPctRevenue),
        unclassifiedOpexPctPointChange: pointChange(current.unclassifiedOpexPctRevenue, prior.unclassifiedOpexPctRevenue),
        totalOpexPctPointChange: pointChange(current.totalOpexPctRevenue, prior.totalOpexPctRevenue),

        operatingProfitChangePct: changePct(
          current.operatingProfitBeforeDepreciation,
          prior.operatingProfitBeforeDepreciation
        )
      };
    },

    calcObservedProfitSensitivity(current, prior) {
      if (!current || !prior) return null;

      const salesChange = this.safeDiv(
        Number(current.revenue) - Number(prior.revenue),
        Math.abs(Number(prior.revenue))
      );

      const opProfitChange = this.safeDiv(
        Number(current.operatingProfitBeforeDepreciation) - Number(prior.operatingProfitBeforeDepreciation),
        Math.abs(Number(prior.operatingProfitBeforeDepreciation))
      );

      if (salesChange == null || opProfitChange == null) return null;
      if (Math.abs(salesChange) < 0.0001) return null;

      return opProfitChange / salesChange;
    },

    calcEstimatedDol(period) {
      if (!period) return null;

      // True DOL requires contribution margin.
      // We do not have real contribution margin in this OPEX-only modal yet.
      // Later, when Margin Quality from OE history is added, set:
      //
      // period.estimatedContributionMargin =
      //   sales - standardCost - estimatedFreight - commission - knownRebates
      //
      const contributionMargin = Number(period.estimatedContributionMargin);

      const operatingProfit = Number(period.operatingProfitBeforeDepreciation);

      if (!isFinite(contributionMargin) || !isFinite(operatingProfit)) return null;
      if (Math.abs(operatingProfit) < 0.0001) return null;

      return contributionMargin / operatingProfit;
    },

    buildOperatingProfitBridge(current, prior) {
      if (!current || !prior) return null;

      const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

      const startingProfit = round2(prior.operatingProfitBeforeDepreciation);
      const endingProfit = round2(current.operatingProfitBeforeDepreciation);

      const revenueImpact = round2(current.revenue - prior.revenue);

      // This explains what happened between Net Sales and Gross Profit After 263A.
      // In other words: COGS, margin, production variances, 263A, etc.
      const grossProfitAfter263AChange = round2(
        current.grossProfitAfter263A - prior.grossProfitAfter263A
      );

      const marginCogs263aImpact = round2(
        grossProfitAfter263AChange - revenueImpact
      );

      // These are signed OPEX impacts.
      // If fixed expense gets worse, this number will be negative.
      const fixedOpexImpact = round2(current.fixedOpex - prior.fixedOpex);
      const variableOpexImpact = round2(current.variableOpex - prior.variableOpex);
      const unclassifiedOpexImpact = round2(current.unclassifiedOpex - prior.unclassifiedOpex);

      // Net operating expenses may include credits or anything not captured above.
      const netOpexChange = round2(
        current.netOperatingExpenses - prior.netOperatingExpenses
      );

      const otherOpexCreditsImpact = round2(
        netOpexChange -
        fixedOpexImpact -
        variableOpexImpact -
        unclassifiedOpexImpact
      );

      const depreciationAddbackImpact = round2(
        current.depreciationAddback - prior.depreciationAddback
      );

      const calculatedEndingProfit = round2(
        startingProfit +
        revenueImpact +
        marginCogs263aImpact +
        fixedOpexImpact +
        variableOpexImpact +
        unclassifiedOpexImpact +
        otherOpexCreditsImpact +
        depreciationAddbackImpact
      );

      const checkDifference = round2(endingProfit - calculatedEndingProfit);

      return {
        startingProfit,
        revenueImpact,
        marginCogs263aImpact,
        fixedOpexImpact,
        variableOpexImpact,
        unclassifiedOpexImpact,
        otherOpexCreditsImpact,
        depreciationAddbackImpact,
        endingProfit,
        calculatedEndingProfit,
        checkDifference,

        rows: [
          {
            key: "starting_profit",
            label: "LYTD Operating Profit Before Depreciation",
            amount: startingProfit,
            type: "start"
          },
          {
            key: "revenue_impact",
            label: "Net Sales Change",
            amount: revenueImpact,
            type: "impact"
          },
          {
            key: "margin_cogs_263a_impact",
            label: "COGS / Margin / 263A Impact",
            amount: marginCogs263aImpact,
            type: "impact"
          },
          {
            key: "fixed_opex_impact",
            label: "Fixed OPEX Impact",
            amount: fixedOpexImpact,
            type: "impact"
          },
          {
            key: "variable_opex_impact",
            label: "Variable OPEX Impact",
            amount: variableOpexImpact,
            type: "impact"
          },
          {
            key: "unclassified_opex_impact",
            label: "Unclassified OPEX Impact",
            amount: unclassifiedOpexImpact,
            type: "impact"
          },
          {
            key: "other_opex_credits_impact",
            label: "OPEX Credits / Other Impact",
            amount: otherOpexCreditsImpact,
            type: "impact"
          },
          {
            key: "depreciation_addback_impact",
            label: "Depreciation Addback Impact",
            amount: depreciationAddbackImpact,
            type: "impact"
          },
          {
            key: "ending_profit",
            label: "YTD Operating Profit Before Depreciation",
            amount: endingProfit,
            type: "end"
          }
        ]
      };
    },

    bridgeRowClass(row) {
      if (!row) return "";
      return {
        "row-bold": row.type === "start" || row.type === "end",
        "row-double-top": row.type === "end"
      };
    },

    formatBridgeImpact(v) {
      const n = Number(v);
      if (!isFinite(n)) return "-";
      const formatted = this.formatMoney(Math.abs(n));
      if (n > 0) return `+${formatted}`;
      if (n < 0) return `(${formatted})`;
      return "0.00";
    },

    printOpexAnalysis() {
      if (!this.reportLoaded || !this.opexAnalysis) {
        this.error = "Run the report before printing OPEX analysis.";
        return;
      }

      const payload = {
        meta: {
          generatedAt: new Date().toISOString(),
          cu: this.cu,
          startDate: this.startDate,
          endDate: this.endDate,

          mtdStartDate: this.mtdReport?.startDate || this.startDate,
          mtdEndDate: this.mtdReport?.endDate || this.endDate,

          ytdStartDate: this.ytdReport?.startDate || `${String(this.startDate).slice(0, 4)}-01-01`,
          ytdEndDate: this.ytdReport?.endDate || this.endDate,

          lytdStartDate: this.lytdReport?.startDate || "",
          lytdEndDate: this.lytdReport?.endDate || ""
        },

        operatingLeverage: {
          estimatedDolMtd: this.opexAnalysis.estimatedDolMtd,
          estimatedDolYtd: this.opexAnalysis.estimatedDolYtd,
          estimatedDolLytd: this.opexAnalysis.estimatedDolLytd,

          observedProfitSensitivityYtdVsLytd: this.opexAnalysis.observedProfitSensitivityYtdVsLytd,
          salesChangePct: this.opexAnalysis.ytdVsLytd?.revenueChangePct,
          operatingProfitChangePct: this.opexAnalysis.ytdVsLytd?.operatingProfitChangePct
        },

        bridge: this.operatingProfitBridge,

        table: {
          mtd: this.opexAnalysis.mtd,
          ytd: this.opexAnalysis.ytd,
          lytd: this.opexAnalysis.lytd,
          ytdVsLytd: this.opexAnalysis.ytdVsLytd
        }
      };

      sessionStorage.setItem("pablo_pnl_opex_analysis_print_payload", JSON.stringify(payload));

      const routeData = this.$router.resolve({
        path: "/print-pnl-opex-analysis"
      });

      window.open(routeData.href, "_blank");
    },

    exportCSV() {
      if (!this.reportLoaded) return;

      const months = this.months || [];
      const rows = [];

      // Header row
      const header = ["Line"];
      for (const m of months) {
        header.push(this.formatMonthLabel(m.month_start));
        if (this.showPercent) header.push("%");
      }
      rows.push(header);

      // Data rows
      for (const r of this.renderedRows) {
        const line = [r.label];

        for (const m of months) {
          const amt = this.cellAmount(r, m);
          const pct = this.showPercent ? this.cellPercent(r, m) : null;

          line.push(amt == null ? "" : Number(amt).toFixed(2));
          if (this.showPercent) line.push(pct == null ? "" : Number(pct).toFixed(2));
        }

        rows.push(line);
      }

      const csv = rows
        .map(r => r.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `pnl_${this.cu}_${this.startDate}_${this.endDate}.csv`
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
/* ✅ PAGE BASE — Pablo black */
.pnl-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

/* ✅ BASE CONTAINER — matches JIMScheduling */
.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #0b0101;
  min-height: 100vh;
}

/* ✅ Header row */
.page-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 10px;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin: 0;
  text-align: center;
}

/* ✅ MAIN LAYOUT */
.main-content {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

/* ✅ Left-side controls */
.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 120px;
}

/* ✅ Right-side workspace */
.right-side-section {
  width: 100%;
  min-width: 0;
}

/* ✅ Scrollable sheet area */
.sheet-scroll-container {
  max-height: 80vh;
  overflow-y: auto;
  width: 100%;
  border-radius: 10px;
  background-color: #0b0101;
  padding-right: 10px;
  box-sizing: border-box;
}

.sheet-scroll-container::-webkit-scrollbar {
  width: 10px;
}
.sheet-scroll-container::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 8px;
}
.sheet-scroll-container::-webkit-scrollbar-track {
  background: #111;
}

/* ✅ Panel base */
.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}

/* ✅ Controls */
.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control input,
.select-input {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

/* ✅ Toggles */
.toggle-row {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.toggle {
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 13px;
  color: #cbbfbf;
}

.toggle input {
  transform: translateY(1px);
}

/* ✅ Buttons */
.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 160px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
  height: 38px;
}

.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

/* ✅ Error / warn boxes */
.error-box {
  border-color: #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
  width: 100%;
}

.warn-box {
  border-color: #888844;
  background: #1f1a0b;
  color: #fef3c7;
  width: 100%;
}

.warn-title {
  font-weight: bold;
  margin-bottom: 6px;
}

.warn-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.warn-chip {
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid #665;
  background: #120f08;
  font-size: 12px;
}

/* ✅ Summary bar */
.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background-color: #708090a4;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 140px;
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 18px;
  opacity: 0.85;
  line-height: 1.1;
}

.summary-value {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
  margin-top: 2px;
  word-break: break-word;
}

/* table container */
.report-panel {
  background-color: #708090a4;
  border-radius: 10px;
  border: 1px solid #888;
}

.hint-panel {
  margin-top: 20px;
  background-color: #0b0101;
}

/* ✅ Table scroll horizontal */
.table-scroll {
  overflow-x: auto;
  padding-bottom: 6px;
}

/* ✅ P&L table */
.pnl-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

/* base cells */
.pnl-table th,
.pnl-table td {
  border: 1px solid #333;
  padding: 6px 8px;
  font-size: 13px;
  text-align: right;
  white-space: nowrap;
}

/* line col left */
.line-col {
  text-align: left !important;
  min-width: 260px;
}

/* header row */
.pnl-table th {
  background-color: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 3;
}

/* sticky left column */
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 4;
  background: #0b0101;
  color: #cbbfbf;
}

/* row hover */
.pnl-table tbody tr:hover td {
  background-color: #5595b7c2;
  color: #fff;
}

/* columns */
.month-col { min-width: 120px; }
.pct-col { min-width: 60px; }

/* indents */
.indent {
  padding-left: 18px;
  display: inline-block;
}

/* formatting rows */
.row-header td {
  background: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
  text-align: left !important;
}

.row-subheader td {
  background: rgba(0,0,0,0.35);
  color: #fff;
  font-weight: bold;
  text-align: left !important;
}

.row-bold td {
  font-weight: bold;
}

.row-double-top td {
  border-top: 3px double #cbbfbf !important;
}

.amt-cell { text-align: right; }
.pct-cell { text-align: right; }

.dash {
  opacity: 0.6;
}

.small-muted {
  font-size: 12px;
  opacity: 0.85;
  color: #fff;
}

/* empty row */
.empty-row {
  padding: 14px;
  font-size: 14px;
  opacity: 0.8;
  text-align: center !important;
}

.clickable-cell {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.clickable-cell:hover {
  background-color: #5595b7c2;
  color: #fff;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 14px;
}

.modal {
  width: min(1200px, 96vw);
  max-height: 92vh;
  overflow: auto;
  border-radius: 12px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.modal-title {
  font-size: 18px;
  font-weight: bold;
}

.modal-close {
  border: 1px solid #888;
  background: #111;
  color: #cbbfbf;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
}

.mini-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

.mini-table th,
.mini-table td {
  border: 1px solid #333;
  padding: 6px 8px;
  font-size: 13px;
}

.mini-table th {
  background-color: #0b0101;
  color: #cbbfbf;
  position: sticky;
  top: 0;
}

.analysis-kpi-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.analysis-kpi {
  flex: 1 1 180px;
  border: 1px solid #555;
  border-radius: 10px;
  padding: 10px;
  background: rgba(0, 0, 0, 0.25);
  text-align: center;
}

.modal-print-btn {
  border: 1px solid #888;
  background: #f3f4f6;
  color: #333;
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
}

.modal-print-btn:hover {
  background: #e5e7eb;
}


/* ===== Executive dashboard modal ===== */
.executive-modal-overlay {
  padding: 10px;
}

.executive-dashboard-modal {
  width: min(1500px, 98vw);
  max-height: 95vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 18px 18px;
  border-color: rgba(105,198,242,0.34);
  background:
    radial-gradient(circle at 100% 0%, rgba(105,198,242,0.12), transparent 24%),
    radial-gradient(circle at 0% 100%, rgba(154,136,201,0.10), transparent 26%),
    linear-gradient(160deg, #10161e, #080a0d 72%);
  box-shadow: 0 30px 100px rgba(0,0,0,0.72);
}

.executive-dashboard-modal::-webkit-scrollbar {
  width: 10px;
}

.executive-dashboard-modal::-webkit-scrollbar-thumb {
  background: #4b5964;
  border-radius: 8px;
}

.executive-dashboard-modal::-webkit-scrollbar-track {
  background: rgba(255,255,255,0.04);
}

.executive-modal-header {
  position: sticky;
  top: -16px;
  z-index: 20;
  margin: -16px -18px 0;
  padding: 16px 18px 13px;
  background: rgba(10,13,17,0.96);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(255,255,255,0.10);
}

.executive-modal-close {
  font-size: 18px;
  min-width: 40px;
  min-height: 40px;
}

.executive-dashboard-in-modal {
  margin-top: 14px;
  margin-bottom: 0;
}

.executive-brief-card {
  min-height: 330px;
}

.executive-talking-points {
  margin-top: 10px;
}

.executive-brief-card .pablo-point {
  padding: 10px 4px;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 10px;
}

.executive-brief-card .pablo-point-text {
  font-size: 14px;
  line-height: 1.32;
}

.executive-brief-footnote {
  margin-top: 10px;
}

/* ===== Executive P&L dashboard ===== */
.executive-dashboard {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 14px;
}

.executive-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.executive-kpi-card {
  position: relative;
  overflow: hidden;
  min-height: 126px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,0.14);
  background:
    radial-gradient(circle at 100% 0%, rgba(85,149,183,0.24), transparent 42%),
    linear-gradient(145deg, rgba(28,35,43,0.98), rgba(8,10,13,0.98));
  color: #fff;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 12px 28px rgba(0,0,0,0.25);
  transition: transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;
  font-family: inherit;
}

.executive-kpi-card:hover {
  transform: translateY(-2px);
  border-color: rgba(129,205,255,0.55);
  box-shadow: 0 16px 34px rgba(0,0,0,0.34);
}

.executive-kpi-card::after {
  content: "";
  position: absolute;
  inset: auto 0 0 0;
  height: 3px;
  background: rgba(255,255,255,0.18);
}

.executive-kpi-card.tone-good::after { background: #66d9a3; }
.executive-kpi-card.tone-bad::after { background: #ff7a86; }
.executive-kpi-card.tone-neutral::after { background: #9aa7b2; }

.executive-kpi-label,
.executive-card-kicker,
.pablo-take-card-kicker,
.pablo-modal-kicker {
  font-family: Arial, sans-serif;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: #8fa4b5;
}

.executive-kpi-value {
  margin-top: 7px;
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 31px;
  line-height: 1;
  font-weight: 700;
  color: #fff;
}

.executive-kpi-change {
  margin-top: 8px;
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #d9e2e8;
}

.tone-good .executive-kpi-arrow { color: #66d9a3; }
.tone-bad .executive-kpi-arrow { color: #ff7a86; }
.tone-neutral .executive-kpi-arrow { color: #9aa7b2; }

.executive-kpi-hint {
  position: absolute;
  right: 12px;
  bottom: 9px;
  font-family: Arial, sans-serif;
  font-size: 9px;
  color: rgba(255,255,255,0.42);
  letter-spacing: 0.04em;
}

.audit-watch-card {
  grid-column: 1 / -1;
  position: relative;
  overflow: hidden;
  padding: 16px 18px 14px;
  border-color: rgba(246, 190, 83, 0.26);
  background:
    radial-gradient(circle at 92% -8%, rgba(246,190,83,0.12), transparent 30%),
    linear-gradient(180deg, rgba(25,24,20,0.98), rgba(9,10,12,0.99));
  box-shadow: 0 16px 38px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.03);
}

.audit-watch-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(246,190,83,0.95), rgba(255,122,134,0.75), transparent);
}

.audit-watch-heading {
  align-items: center;
}

.audit-watch-kicker {
  color: #f6be53;
}

.audit-watch-subtitle {
  margin-top: 4px;
  max-width: 760px;
  font-family: Arial, sans-serif;
  font-size: 10px;
  line-height: 1.45;
  color: rgba(232,240,245,0.58);
}

.audit-watch-status {
  min-width: 178px;
  padding: 9px 11px;
  border-radius: 11px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(4,7,10,0.58);
  font-family: Arial, sans-serif;
  text-align: right;
}

.audit-watch-status strong,
.audit-watch-status > span:last-child {
  display: block;
}

.audit-watch-status strong {
  margin-top: 2px;
  font-size: 10px;
  letter-spacing: 0.08em;
}

.audit-watch-status > span:last-child {
  margin-top: 2px;
  font-size: 9px;
  color: rgba(232,240,245,0.55);
}

.audit-watch-status-dot {
  display: inline-block !important;
  width: 7px;
  height: 7px;
  margin-right: 5px;
  border-radius: 50%;
  vertical-align: middle;
}

.audit-watch-status.review {
  border-color: rgba(246,190,83,0.30);
  color: #f6c86f;
}
.audit-watch-status.review .audit-watch-status-dot {
  background: #f6be53;
  box-shadow: 0 0 9px rgba(246,190,83,0.55);
}

.audit-watch-status.clean {
  border-color: rgba(102,217,163,0.28);
  color: #7ee7b5;
}
.audit-watch-status.clean .audit-watch-status-dot {
  background: #66d9a3;
  box-shadow: 0 0 9px rgba(102,217,163,0.48);
}

.audit-watch-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 13px;
}

.audit-watch-item {
  position: relative;
  overflow: hidden;
  padding: 12px 13px 11px;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 12px;
  background: rgba(6,8,10,0.72);
  color: #e8edf1;
  font-family: Arial, sans-serif;
  text-align: left;
  cursor: pointer;
  transition: transform 0.14s ease, border-color 0.14s ease, background 0.14s ease;
}

.audit-watch-item:hover {
  transform: translateY(-1px);
  background: rgba(16,19,22,0.94);
}

.audit-watch-item.severity-high {
  border-color: rgba(255,122,134,0.30);
  box-shadow: inset 3px 0 0 rgba(255,122,134,0.82);
}

.audit-watch-item.severity-review {
  border-color: rgba(246,190,83,0.24);
  box-shadow: inset 3px 0 0 rgba(246,190,83,0.72);
}

.audit-watch-item-top {
  display: flex;
  align-items: center;
  gap: 9px;
}

.audit-watch-index {
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 900;
  color: rgba(246,190,83,0.82);
  letter-spacing: 0.08em;
}

.audit-watch-name-wrap {
  min-width: 0;
  flex: 1;
}

.audit-watch-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.02em;
  color: #fff;
}

.audit-watch-account {
  margin-top: 2px;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.10em;
  color: rgba(232,240,245,0.42);
}

.audit-watch-severity {
  flex: 0 0 auto;
  padding: 4px 6px;
  border: 1px solid rgba(246,190,83,0.24);
  border-radius: 999px;
  font-size: 7px;
  font-weight: 900;
  letter-spacing: 0.09em;
  color: #f6c86f;
}

.severity-high .audit-watch-severity {
  border-color: rgba(255,122,134,0.30);
  color: #ff98a2;
}

.audit-watch-copy {
  margin-top: 9px;
  min-height: 35px;
  font-size: 10px;
  line-height: 1.45;
  color: rgba(236,241,244,0.80);
}

.audit-watch-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
  margin-top: 10px;
}

.audit-watch-metric {
  padding: 6px 7px 5px;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  background: rgba(255,255,255,0.025);
}

.audit-watch-metric span,
.audit-watch-metric strong {
  display: block;
}

.audit-watch-metric span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 7px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: rgba(232,240,245,0.43);
}

.audit-watch-metric strong {
  margin-top: 2px;
  font-size: 10px;
  color: #eaf0f4;
}

.audit-rate-up { color: #ff98a2 !important; }
.audit-rate-down { color: #7ee7b5 !important; }

.audit-watch-action {
  margin-top: 9px;
  font-size: 8px;
  font-weight: 900;
  letter-spacing: 0.10em;
  color: #f6c86f;
}

.audit-watch-clean-state {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 13px;
  padding: 13px 14px;
  border: 1px solid rgba(102,217,163,0.20);
  border-radius: 11px;
  background: rgba(102,217,163,0.035);
  font-family: Arial, sans-serif;
}

.audit-watch-clean-icon {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  border: 1px solid rgba(102,217,163,0.30);
  border-radius: 50%;
  color: #7ee7b5;
  font-size: 16px;
  font-weight: 900;
}

.audit-watch-clean-state strong,
.audit-watch-clean-state span {
  display: block;
}
.audit-watch-clean-state strong { font-size: 11px; color: #dff9eb; }
.audit-watch-clean-state span { margin-top: 2px; font-size: 9px; color: rgba(232,240,245,0.55); }

.audit-watch-footnote {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(255,255,255,0.07);
  font-family: Arial, sans-serif;
  font-size: 8px;
  line-height: 1.4;
  color: rgba(232,240,245,0.40);
}

.executive-chart-grid,
.executive-bottom-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.executive-chart-card {
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 14px;
  padding: 14px 16px 12px;
  background:
    linear-gradient(180deg, rgba(26,31,38,0.96), rgba(9,11,14,0.98));
  box-shadow: 0 12px 28px rgba(0,0,0,0.22);
  min-width: 0;
}

.cumulative-margin-card {
  grid-column: 1 / -1;
  position: relative;
  overflow: hidden;
  padding: 16px 18px 12px;
  background:
    radial-gradient(circle at 88% -10%, rgba(105,198,242,0.17), transparent 32%),
    radial-gradient(circle at 72% 0%, rgba(154,136,201,0.12), transparent 28%),
    linear-gradient(180deg, rgba(23,29,36,0.98), rgba(8,10,13,0.99));
  border-color: rgba(105,198,242,0.24);
  box-shadow: 0 16px 38px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.03);
}

.cumulative-margin-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent 0%, #69c6f2 28%, #9a88c9 72%, transparent 100%);
  opacity: 0.85;
}

.cumulative-heading-row {
  align-items: center;
}

.cumulative-kicker {
  color: #8dd8fb;
}

.cumulative-chart-subtitle {
  margin-top: 4px;
  max-width: 620px;
  font-family: Arial, sans-serif;
  font-size: 10px;
  line-height: 1.4;
  color: rgba(232,240,245,0.55);
}

.cumulative-margin-summary {
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.cumulative-end-pill,
.cumulative-spread-pill {
  min-width: 92px;
  padding: 7px 10px 6px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(4,7,10,0.55);
  font-family: Arial, sans-serif;
  text-align: right;
}

.cumulative-end-pill span,
.cumulative-spread-pill span {
  display: block;
  margin-bottom: 2px;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.10em;
  color: rgba(229,237,242,0.55);
}

.cumulative-end-pill strong,
.cumulative-spread-pill strong {
  display: block;
  font-size: 16px;
  line-height: 1;
  color: #fff;
}

.cumulative-end-pill.current {
  border-color: rgba(105,198,242,0.36);
  box-shadow: inset 0 0 18px rgba(105,198,242,0.05);
}

.cumulative-end-pill.current strong { color: #8dd8fb; }
.cumulative-end-pill.prior { border-color: rgba(154,136,201,0.34); }
.cumulative-end-pill.prior strong { color: #b9abe3; }

.cumulative-spread-pill.tone-good strong { color: #66d9a3; }
.cumulative-spread-pill.tone-bad strong { color: #ff7a86; }
.cumulative-spread-pill.tone-neutral strong { color: #cbd5dc; }

.cumulative-chart-shell {
  position: relative;
  margin-top: 2px;
}

.cumulative-chart-legend {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
}

.cumulative-margin-chart {
  height: 260px;
  margin-top: 0;
}

.cumulative-current-line {
  stroke-width: 3.5;
}

.chart-last-ring {
  fill: rgba(0,0,0,0.22);
  stroke-width: 1.5;
}

.chart-last-ring-ty {
  stroke: rgba(105,198,242,0.72);
  filter: drop-shadow(0 0 7px rgba(105,198,242,0.40));
}

.chart-last-ring-ly {
  stroke: rgba(185,171,227,0.66);
}

.chart-end-label {
  font-family: Arial, sans-serif;
  font-size: 10px;
  font-weight: 900;
  paint-order: stroke;
  stroke: #0a0d10;
  stroke-width: 3px;
  stroke-linejoin: round;
}

.chart-end-label-ty { fill: #8dd8fb; }
.chart-end-label-ly { fill: #b9abe3; }

.executive-card-heading-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.executive-card-title {
  margin-top: 3px;
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 22px;
  font-weight: 700;
  color: #fff;
}

.chart-legend {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
  font-family: Arial, sans-serif;
  font-size: 10px;
  color: #cbd5dc;
}

.chart-legend span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.legend-line {
  display: inline-block;
  width: 18px;
  height: 2px;
  border-radius: 999px;
}

.legend-ty { background: #69c6f2; }
.legend-ly { background: #9a88c9; }

.executive-line-chart {
  width: 100%;
  height: 230px;
  margin-top: 5px;
  overflow: visible;
}

.chart-grid-line {
  stroke: rgba(255,255,255,0.10);
  stroke-width: 1;
}

.chart-axis-label,
.chart-x-label {
  fill: rgba(229,237,242,0.64);
  font-family: Arial, sans-serif;
  font-size: 10px;
}

.chart-line {
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.chart-line-ty {
  stroke: #69c6f2;
  filter: drop-shadow(0 0 5px rgba(105,198,242,0.26));
}

.chart-line-ly {
  stroke: #9a88c9;
  stroke-width: 2;
  stroke-dasharray: 6 5;
  opacity: 0.82;
}

.chart-dot { stroke-width: 2; }
.chart-dot-ty { fill: #69c6f2; stroke: #d8f3ff; }
.chart-dot-ly { fill: #9a88c9; stroke: #e2dcf2; opacity: 0.9; }

.movers-card { min-height: 330px; }

.mover-legend {
  font-family: Arial, sans-serif;
  font-size: 10px;
  color: rgba(255,255,255,0.50);
}

.movers-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-top: 12px;
}

.mover-column-title {
  font-family: Arial, sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.mover-column-title.pressure { color: #ff8f99; }
.mover-column-title.relief { color: #71dda9; }

.mover-row {
  display: block;
  width: 100%;
  padding: 8px 0;
  border: 0;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  background: transparent;
  color: #fff;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
}

.mover-row:hover .mover-name { color: #9edcff; }

.mover-row-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: baseline;
}

.mover-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: #e7edf1;
}

.mover-value {
  flex: 0 0 auto;
  font-family: Arial, sans-serif;
  font-size: 11px;
  font-weight: 800;
}

.mover-value.bad { color: #ff8f99; }
.mover-value.good { color: #71dda9; }

.mover-track {
  height: 4px;
  border-radius: 99px;
  background: rgba(255,255,255,0.08);
  overflow: hidden;
  margin-top: 6px;
}

.mover-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
}

.bad-fill { background: linear-gradient(90deg, #a73c48, #ff7a86); }
.good-fill { background: linear-gradient(90deg, #2f8f68, #66d9a3); }

.mover-meta {
  margin-top: 4px;
  font-family: Arial, sans-serif;
  font-size: 9px;
  color: rgba(255,255,255,0.42);
}

.empty-mover {
  padding: 18px 0;
  font-size: 12px;
  color: rgba(255,255,255,0.42);
}

.pablo-take-card {
  position: relative;
  overflow: hidden;
  min-height: 330px;
  padding: 28px;
  border-radius: 14px;
  border: 1px solid rgba(105,198,242,0.34);
  background:
    radial-gradient(circle at 72% 28%, rgba(105,198,242,0.22), transparent 22%),
    radial-gradient(circle at 35% 78%, rgba(154,136,201,0.20), transparent 27%),
    linear-gradient(145deg, #121923, #090b0f 70%);
  color: #fff;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  box-shadow: inset 0 0 40px rgba(105,198,242,0.04), 0 12px 30px rgba(0,0,0,0.25);
  transition: transform 0.18s ease, border-color 0.18s ease;
}

.pablo-take-card:hover {
  transform: translateY(-2px);
  border-color: rgba(105,198,242,0.75);
}

.pablo-take-card-title {
  margin-top: 12px;
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: clamp(38px, 4vw, 62px);
  line-height: 0.94;
  letter-spacing: -0.02em;
}

.pablo-take-card-copy {
  position: relative;
  z-index: 2;
  max-width: 430px;
  margin-top: 18px;
  font-size: 16px;
  line-height: 1.45;
  color: #c9d2d8;
}

.pablo-take-card-action {
  position: absolute;
  left: 28px;
  bottom: 26px;
  font-family: Arial, sans-serif;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.12em;
  color: #86d8ff;
}

.pablo-orbit {
  position: absolute;
  border: 1px solid rgba(105,198,242,0.18);
  border-radius: 50%;
  pointer-events: none;
}

.pablo-orbit-one {
  width: 240px;
  height: 240px;
  right: -60px;
  top: -50px;
}

.pablo-orbit-two {
  width: 330px;
  height: 330px;
  right: -110px;
  top: -100px;
  border-color: rgba(154,136,201,0.14);
}

.pablo-take-btn {
  background: linear-gradient(135deg, #dff5ff, #dcd5f5);
  border-color: #a9cee0;
  letter-spacing: 0.05em;
}

.pablo-take-modal {
  width: min(980px, 96vw);
  border-color: rgba(105,198,242,0.42);
  background:
    radial-gradient(circle at 100% 0%, rgba(105,198,242,0.16), transparent 28%),
    linear-gradient(160deg, #111821, #090b0f 68%);
  box-shadow: 0 28px 90px rgba(0,0,0,0.62);
}

.pablo-modal-header {
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(255,255,255,0.10);
}

.pablo-modal-title {
  margin-top: 3px;
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 38px;
  color: #fff;
}

.pablo-modal-subtitle { margin-top: 3px !important; }

.pablo-talking-points {
  display: flex;
  flex-direction: column;
  margin-top: 8px;
}

.pablo-point {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr);
  gap: 14px;
  width: 100%;
  padding: 15px 8px;
  border: 0;
  border-bottom: 1px solid rgba(255,255,255,0.09);
  background: transparent;
  color: #fff;
  text-align: left;
  font-family: inherit;
}

.pablo-point.clickable { cursor: pointer; }
.pablo-point.clickable:hover { background: rgba(105,198,242,0.07); }
.pablo-point.clickable:hover .pablo-point-action { color: #a7e5ff; }

.pablo-point-number {
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: 900;
  color: #69c6f2;
  letter-spacing: 0.08em;
  padding-top: 3px;
}

.pablo-point-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pablo-point-text {
  font-size: 17px;
  line-height: 1.36;
  color: #eef3f6;
}

.pablo-point-action {
  font-family: Arial, sans-serif;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 0.10em;
  color: rgba(105,198,242,0.65);
}

.pablo-modal-footnote {
  margin-top: 12px;
  font-family: Arial, sans-serif;
  font-size: 10px;
  color: rgba(255,255,255,0.42);
}

.pnl-focus-pulse td {
  animation: pnlPulse 0.7s ease-in-out 3;
}

@keyframes pnlPulse {
  0%, 100% { background-color: inherit; }
  50% { background-color: rgba(105,198,242,0.28); color: #fff; }
}

@media (max-width: 1150px) {
  .audit-watch-grid { grid-template-columns: 1fr; }
  .audit-watch-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .executive-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .executive-chart-grid,
  .executive-bottom-grid { grid-template-columns: 1fr; }
}

@media (max-width: 760px) {
  .executive-kpi-grid { grid-template-columns: 1fr; }
  .movers-columns { grid-template-columns: 1fr; }
  .executive-line-chart { height: 190px; }
  .cumulative-heading-row { align-items: flex-start; flex-direction: column; }
  .cumulative-margin-summary { width: 100%; justify-content: flex-start; }
  .cumulative-end-pill,
  .cumulative-spread-pill { flex: 1 1 90px; text-align: left; }
  .cumulative-chart-legend { position: static; justify-content: flex-start; margin-top: 10px; }
  .cumulative-margin-chart { height: 210px; }
}

/* ✅ PRINT */
@media print {
  .left-side-section,
  .error-box,
  .warn-box {
    display: none !important;
  }

  .main-content {
    display: block !important;
    padding: 0 !important;
  }

  .sheet-scroll-container {
    max-height: none !important;
    overflow: visible !important;
    padding-right: 0 !important;
  }

  .jim-scheduling-container,
  .panel,
  .summary-bar {
    background: white !important;
    color: black !important;
    border: none !important;
  }

  .pnl-table th {
    background: #eee !important;
    color: black !important;
  }

  .sticky-col {
    background: white !important;
    color: black !important;
  }

  .pnl-table td, .pnl-table th {
    border: 1px solid #999 !important;
  }
}
</style>
